import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, IconButton, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, List, ListItem, ListItemText, CircularProgress, Divider, Paper, SvgIcon, TextField, ListItemButton } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { autoTagScript } from '../services/scriptService';
import api from '../utils/api';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import {
  SCENE_PREFIXES,
  TRANSITIONS,
  normalizeNewlines,
  isTransitionLine,
  standardizeExtension
} from '../utils/fountainParser';
import {
  MEASUREMENTS,
  CONTENT_WIDTH,
  DIALOGUE_RIGHT_MARGIN,
  baseStyles,
  elementStyles,
  darkModeColors,
  darkModeElementStyles,
  TAG_TYPES,
  QUICK_INSERTS,
  QUICK_INSERT_SNIPPETS,
  TRANSITION_OPTIONS,
  TITLE_PAGE_TEMPLATE,
  TIME_OF_DAY_OPTIONS,
  AC_PREFIX_RE,
  AC_LOCATION_RE,
  AC_TIME_RE,
  AC_CHARACTER_RE
} from '../utils/screenplayConstants';

// Custom spray can icon for tag toggling
const SprayCanIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M10 2h4v2h1.5a1.5 1.5 0 011.5 1.5V7h-1v13a2 2 0 01-2 2h-4a2 2 0 01-2-2V7H7V5.5A1.5 1.5 0 018.5 4H10V2z" />
    <rect x="9" y="8" width="6" height="10" rx="0.8" />
    <circle cx="17.5" cy="4" r="0.7" />
  </SvgIcon>
);

// MEASUREMENTS, CONTENT_WIDTH, DIALOGUE_RIGHT_MARGIN are imported from
// ../utils/screenplayConstants.js — single canonical definitions.

// QUICK_INSERTS and QUICK_INSERT_SNIPPETS are imported from screenplayConstants.js.

const sanitizeCharacterName = (name = '') => name.replace(/\([^)]*\)/g, '').replace(/\s*\^$/, '').trim();

const coverPageToText = (cover = {}) => {
  const parts = [];
  if (cover.title) parts.push(`Title: ${cover.title}`);
  if (cover.credit) parts.push(`Credit: ${cover.credit}`);
  if (cover.author) parts.push(`Author: ${cover.author}`);
  if (cover.source) parts.push(`Source: ${cover.source}`);
  if (cover.draft) parts.push(`Draft date: ${cover.draft}`);
  if (cover.contact) parts.push(`Contact: ${cover.contact}`);
  if (cover.copyright) parts.push(`Copyright: ${cover.copyright}`);
  return parts.join('\n');
};

const extractCharacterNames = (content = '') => {
  const normalized = normalizeNewlines(content).replace(/\/\*[\s\S]*?\*\//g, '');
  const lines = normalized.split('\n');
  const names = new Set();
  lines.forEach((line, i) => {
    let trimmed = line.trim();
    if (!trimmed) return;
    if (trimmed.length > 50) return;

    // Fountain forced-character marker: @CharName
    const isForced = trimmed.startsWith('@');
    if (isForced) {
      trimmed = trimmed.substring(1).trim();
    }

    const upper = trimmed.toUpperCase();
    if (SCENE_PREFIXES.some(prefix => upper.startsWith(prefix))) return;
    if (isTransitionLine(upper)) return;
    if (/^(title|author|draft date|contact|copyright|source|notes|credit):/i.test(trimmed)) return;
    // Skip common non-character all-caps words
    if (/^(FADE IN|FADE OUT|THE END|CONTINUED|MORE)/.test(upper)) return;

    // Check if this looks like a character name (ALL-CAPS, optional parenthetical extension,
    // optional trailing ^ dual-dialogue marker)
    const characterMatch = trimmed.match(/^[A-Z][A-Z0-9\s\-']{1,28}(?:\s*\([^)]*\))?(?:\s*\^)?$/);
    if (!characterMatch && !isForced) return;

    // Validate context: preceded by blank line OR forced marker OR followed by dialogue-like content
    const prevLine = i > 0 ? lines[i - 1].trim() : '';
    const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';
    const hasBlankBefore = i === 0 || prevLine === '';
    const hasDialogueAfter = nextLine !== '' && !nextLine.startsWith('INT') && !nextLine.startsWith('EXT');

    if (isForced || hasBlankBefore || hasDialogueAfter) {
      const cleanName = sanitizeCharacterName(trimmed).toUpperCase();
      if (cleanName.length >= 2) {
        names.add(cleanName);
      }
    }
  });
  return Array.from(names).sort();
};

// SCENE_PREFIXES is imported from fountainParser.js — single canonical list.

// TIME_OF_DAY_OPTIONS and AC_*_RE regexes are imported from screenplayConstants.js.

const extractSceneMetadata = (content = '') => {
  const normalized = normalizeNewlines(content).replace(/\/\*[\s\S]*?\*\//g, '');
  const lines = normalized.split('\n');
  const locations = new Set();
  const times = new Set();
  const sceneRegex = /^(INT\.|EXT\.|INT\/EXT\.|I\/E\.|EST\.|INT\.\/?EXT\.)\s+(.+?)(?:\s*-\s*(.+))?$/i;
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const match = trimmed.match(sceneRegex);
    if (!match) return;
    const location = match[2]?.trim();
    if (location) {
      locations.add(location.toUpperCase());
    }
    const time = match[3]?.trim();
    if (time) {
      times.add(time.toUpperCase());
    }
  });
  return {
    locations: Array.from(locations).sort(),
    times: Array.from(times).sort()
  };
};

// Render Fountain emphasis markers as React elements
// Supports: ***bold italic***, **bold**, *italic*, _underline_
// Handles escaped characters (\* \_ ) and avoids false positives
const renderEmphasis = (text) => {
  if (!text || typeof text !== 'string') return text;
  // Quick bail if no emphasis markers present
  if (!/[*_]/.test(text)) return text;

  let keyIdx = 0;
  // Escape \* and \_ so they don't trigger emphasis
  const escaped = text.replace(/\\\*/g, '\u0000').replace(/\\_/g, '\u0001');

  // Single combined regex — order matters: ***first, then **, then *, then _
  const re = /\*{3}(.+?)\*{3}|\*{2}(.+?)\*{2}|\*(.+?)\*|_(.+?)_/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = re.exec(escaped)) !== null) {
    // Plain text before this match
    if (match.index > lastIndex) {
      parts.push(escaped.slice(lastIndex, match.index).replace(/\u0000/g, '*').replace(/\u0001/g, '_'));
    }

    if (match[1] != null) {
      // ***bold italic***
      const inner = match[1].replace(/\u0000/g, '*').replace(/\u0001/g, '_');
      parts.push(<span key={keyIdx++} style={{ fontWeight: 'bold', fontStyle: 'italic' }}>{inner}</span>);
    } else if (match[2] != null) {
      // **bold**
      const inner = match[2].replace(/\u0000/g, '*').replace(/\u0001/g, '_');
      parts.push(<span key={keyIdx++} style={{ fontWeight: 'bold' }}>{inner}</span>);
    } else if (match[3] != null) {
      // *italic*
      const inner = match[3].replace(/\u0000/g, '*').replace(/\u0001/g, '_');
      parts.push(<span key={keyIdx++} style={{ fontStyle: 'italic' }}>{inner}</span>);
    } else if (match[4] != null) {
      // _underline_
      const inner = match[4].replace(/\u0000/g, '*').replace(/\u0001/g, '_');
      parts.push(<span key={keyIdx++} style={{ textDecoration: 'underline' }}>{inner}</span>);
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining plain text
  if (lastIndex < escaped.length) {
    parts.push(escaped.slice(lastIndex).replace(/\u0000/g, '*').replace(/\u0001/g, '_'));
  }

  return parts.length > 0 ? parts : text;
};

// Element styles, dark mode colors/styles, and TAG_TYPES are imported from
// ../utils/screenplayConstants.js — single canonical definitions.
// Re-export TAG_TYPES so existing imports from this file continue to work.
export { TAG_TYPES } from '../utils/screenplayConstants';

// TRANSITIONS, isTransitionLine, SCENE_PREFIXES, and CHARACTER_EXTENSIONS
// are imported from ../utils/fountainParser.js — single canonical definitions.

// Measure actual rendered heights of screenplay elements using a hidden DOM container.
// Renders ALL elements at once (like a real page) and uses getBoundingClientRect()
// for sub-pixel precision. Returns an array of pixel heights for each element.
// Results are cached by a content hash so repeated pagination of unchanged content
// skips the expensive DOM measurement.
const _heightCache = new Map();
const HEIGHT_CACHE_LIMIT = 8;
const measureElementHeights = (lines) => {
  // Build a lightweight hash from line type + text — enough to detect changes.
  const hash = lines.map(l => `${l.type}:${l.text || ''}|${l.left ? l.left.length : 0}|${l.right ? l.right.length : 0}`).join('\n');
  const cached = _heightCache.get(hash);
  if (cached) return cached;

  const contentWidthIn = MEASUREMENTS.PAGE_WIDTH - MEASUREMENTS.LEFT_MARGIN - MEASUREMENTS.RIGHT_MARGIN;

  // Create hidden measurement container styled identically to the page content area
  const container = document.createElement('div');
  container.style.cssText = `
    position: absolute; left: -9999px; top: 0;
    width: ${contentWidthIn}in;
    visibility: hidden;
    box-sizing: content-box;
    margin: 0; padding: 0;
    font-family: 'Courier Prime, Courier, monospace';
    font-size: 12pt;
    line-height: 1.19;
    white-space: pre-wrap;
  `;
  document.body.appendChild(container);

  // Render all elements into the container at once
  const elements = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const el = document.createElement('div');

    // Apply base styles explicitly (not via Object.assign from React style objects)
    el.style.fontFamily = 'Courier Prime, Courier, monospace';
    el.style.fontSize = '12pt';
    el.style.lineHeight = '1.19';
    el.style.whiteSpace = 'pre-wrap';
    el.style.margin = '0';
    el.style.padding = '0';

    // Apply type-specific styles
    if (line.type === 'blank') {
      el.style.height = '14.28pt';
      el.innerHTML = '&nbsp;';
    } else if (line.type === 'section' || line.type === 'synopsis') {
      // Structural markers — not rendered on the page, zero height
      el.style.display = 'none';
    } else if (line.type === 'scene') {
      el.style.textTransform = 'uppercase';
      el.textContent = line.text || '';
    } else if (line.type === 'character') {
      el.style.textTransform = 'uppercase';
      el.style.marginLeft = `${MEASUREMENTS.CHARACTER_INDENT}in`;
      el.textContent = line.text || '';
    } else if (line.type === 'parenthetical') {
      el.style.marginLeft = `${MEASUREMENTS.PARENTHETICAL_INDENT}in`;
      el.style.marginRight = `${MEASUREMENTS.PARENTHETICAL_INDENT}in`;
      el.textContent = line.text || '';
    } else if (line.type === 'dialogue') {
      el.style.marginLeft = `${MEASUREMENTS.DIALOGUE_INDENT}in`;
      el.style.marginRight = `${DIALOGUE_RIGHT_MARGIN}in`;
      el.textContent = line.text || '';
    } else if (line.type === 'lyric') {
      el.style.fontStyle = 'italic';
      el.style.marginLeft = `${MEASUREMENTS.DIALOGUE_INDENT}in`;
      el.style.marginRight = `${DIALOGUE_RIGHT_MARGIN}in`;
      el.textContent = line.text || '';
    } else if (line.type === 'transition') {
      el.style.textAlign = 'right';
      el.style.marginRight = '0';
      el.style.textTransform = 'uppercase';
      el.textContent = line.text || '';
    } else if (line.type === 'centered') {
      el.style.textAlign = 'center';
      el.textContent = line.text || '';
    } else if (line.type === 'dualDialogue') {
      // Render two columns side by side and take the max height
      el.style.display = 'flex';
      el.style.gap = '0.3in';
      const colWidth = `calc(50% - 0.15in)`;
      const renderCol = (lines) => {
        const col = document.createElement('div');
        col.style.width = colWidth;
        col.style.flexShrink = '0';
        for (const colLine of lines) {
          const row = document.createElement('div');
          row.style.fontFamily = 'Courier Prime, Courier, monospace';
          row.style.fontSize = '12pt';
          row.style.lineHeight = '1.19';
          row.style.whiteSpace = 'pre-wrap';
          row.style.margin = '0';
          row.style.padding = '0';
          if (colLine.type === 'character') {
            row.style.textTransform = 'uppercase';
            row.style.marginLeft = `${MEASUREMENTS.CHARACTER_INDENT * 0.45}in`;
          } else if (colLine.type === 'parenthetical') {
            row.style.marginLeft = `${MEASUREMENTS.PARENTHETICAL_INDENT * 0.45}in`;
          } else if (colLine.type === 'dialogue') {
            row.style.marginLeft = `${MEASUREMENTS.DIALOGUE_INDENT * 0.45}in`;
          }
          row.textContent = colLine.text || '';
          col.appendChild(row);
        }
        return col;
      };
      el.appendChild(renderCol(line.left || []));
      el.appendChild(renderCol(line.right || []));
    } else {
      // action, note, etc.
      el.textContent = line.text || '';
    }

    container.appendChild(el);
    elements.push(el);
  }

  // Measure using getBoundingClientRect for sub-pixel precision
  const heights = [];
  for (let i = 0; i < elements.length; i++) {
    const rect = elements[i].getBoundingClientRect();
    heights.push(rect.height);
  }

  document.body.removeChild(container);

  // Cache result — evict oldest entries to bound memory
  if (_heightCache.size >= HEIGHT_CACHE_LIMIT) {
    const firstKey = _heightCache.keys().next().value;
    _heightCache.delete(firstKey);
  }
  _heightCache.set(hash, heights);
  return heights;
};

// Paginate using pre-measured pixel heights for each element.
// Content area = PAGE_HEIGHT - TOP_MARGIN - BOTTOM_MARGIN in pixels (96 DPI).
const calculatePages = (lines, heights) => {
  const contentAreaPx = (MEASUREMENTS.PAGE_HEIGHT - MEASUREMENTS.TOP_MARGIN - MEASUREMENTS.BOTTOM_MARGIN) * 96;
  let pages = [];
  let currentPage = [];
  let currentHeight = 0;
  let dialogueBlock = null;
  let sceneStartIndex = null;
  let sceneOriginalIndex = null;

  const getHeight = (idx) => (heights && heights[idx] != null) ? heights[idx] : 19;

  const isDialogueElement = (type) => {
    return ['character', 'parenthetical', 'dialogue', 'lyric'].includes(type);
  };

  const lookAhead = (startIndex, count) => {
    let result = [];
    for (let j = startIndex; j < Math.min(startIndex + count, lines.length); j++) {
      result.push(lines[j]);
    }
    return result;
  };

  const shouldStartNewPage = (usedHeight, nextLine, nextIdx) => {
    if (usedHeight >= contentAreaPx) return true;

    // Scene heading protection: don't orphan a scene heading near the bottom.
    // Hollywood standard: keep at least 2 lines following the slugline.
    if (nextLine.type === 'scene') {
      const nextLines = lookAhead(nextIdx + 1, 3);
      // Need room for heading + at least 2 following lines (~38px at 12pt/1.19lh)
      const headingH = getHeight(nextIdx);
      const followH = nextLines.slice(0, 2).reduce((sum, _, fi) => sum + getHeight(nextIdx + 1 + fi), 0);
      const minNeeded = headingH + (followH || 38); // heading + 2 lines (fallback ~38px)
      if (usedHeight + minNeeded > contentAreaPx && nextLines.length > 0) {
        return true;
      }
    }

    // Don't break immediately after a scene heading — need at least 2 lines after
    if (currentPage.length > 0 &&
        currentPage[currentPage.length - 1].type === 'scene') {
      const remaining = contentAreaPx - usedHeight;
      if (remaining < 38) return true; // less than ~2 lines left
    }

    return false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const h = getHeight(i);

    // Forced page break: push current page and start fresh
    if (line.type === 'pageBreak') {
      if (dialogueBlock) {
        if (currentPage.length) pages.push([...currentPage]);
        currentPage = [...dialogueBlock.elements];
      }
      if (currentPage.length) {
        pages.push([...currentPage]);
      }
      currentPage = [];
      currentHeight = 0;
      dialogueBlock = null;
      sceneStartIndex = null;
      sceneOriginalIndex = null;
      continue;
    }

    // Track scene starts (store both page position and original index for height lookup)
    if (line.type === 'scene') {
      sceneStartIndex = currentPage.length;
      sceneOriginalIndex = i;
    }

    // Dual dialogue compound element — treat as atomic block
    if (line.type === 'dualDialogue') {
      if (dialogueBlock) {
        currentPage.push(...dialogueBlock.elements);
        currentHeight += dialogueBlock.totalHeight;
        dialogueBlock = null;
      }
      if (shouldStartNewPage(currentHeight, line, i)) {
        if (currentPage.length) pages.push([...currentPage]);
        currentPage = [line];
        currentHeight = h;
      } else {
        currentPage.push(line);
        currentHeight += h;
      }
      continue;
    }

    // Handle dialogue blocks — accumulate character + dialogue + parenthetical
    if (line.type === 'character') {
      dialogueBlock = {
        character: line,
        characterIdx: i,
        elements: [line],
        indices: [i],
        totalHeight: h
      };
      continue;
    }

    if (dialogueBlock && isDialogueElement(line.type)) {
      dialogueBlock.elements.push(line);
      dialogueBlock.indices.push(i);
      dialogueBlock.totalHeight += h;
      continue;
    }

    // Process complete dialogue block when we hit a non-dialogue element
    if (dialogueBlock && !isDialogueElement(line.type)) {
      const remaining = contentAreaPx - currentHeight;
      // If the whole block fits, place it
      if (dialogueBlock.totalHeight <= remaining || currentPage.length === 0) {
        currentPage.push(...dialogueBlock.elements);
        currentHeight += dialogueBlock.totalHeight;
      } else {
        // Dialogue block doesn't fit — split it with (MORE) / (CONT'D)
        // Need room for character cue + at least 1 dialogue line + (MORE)
        const charH = getHeight(dialogueBlock.characterIdx);
        const moreH = charH; // (MORE) is same height as a character line
        const minForSplit = charH + 19 + moreH; // character + 1 line + (MORE)
        if (remaining < minForSplit) {
          // Not enough room even for character + 1 line + MORE — move whole block
          pages.push([...currentPage]);
          currentPage = [...dialogueBlock.elements];
          currentHeight = dialogueBlock.totalHeight;
        } else {
          // Split: place as many elements as fit, add (MORE)
          const placed = [dialogueBlock.elements[0]]; // character cue
          let usedH = charH;
          for (let di = 1; di < dialogueBlock.elements.length; di++) {
            const elH = getHeight(dialogueBlock.indices[di]);
            // Reserve space for (MORE) line
            if (usedH + elH + moreH > remaining) break;
            placed.push(dialogueBlock.elements[di]);
            usedH += elH;
          }
          // Add (MORE) marker
          placed.push({
            key: `more-${dialogueBlock.characterIdx}`,
            type: 'character',
            text: '(MORE)',
            isMore: true
          });
          currentPage.push(...placed);
          currentHeight += usedH + moreH;

          // Start new page with CHARACTER (CONT'D) + remaining elements
          pages.push([...currentPage]);
          const characterName = dialogueBlock.character.text.replace(/\s*\([^)]*\)$/, '').trim();
          const existingExt = dialogueBlock.character.extension || '';
          const contdExt = existingExt ? ` ${existingExt} (CONT'D)` : " (CONT'D)";
          const continuedCharacter = {
            ...dialogueBlock.character,
            key: `contd-${dialogueBlock.characterIdx}`,
            text: characterName + contdExt,
            isContinued: true
          };
          currentPage = [continuedCharacter];
          currentHeight = charH;
          // Add remaining dialogue elements
          const placedCount = placed.length - 2; // minus character + MORE
          for (let di = 1 + placedCount; di < dialogueBlock.elements.length; di++) {
            currentPage.push(dialogueBlock.elements[di]);
            currentHeight += getHeight(dialogueBlock.indices[di]);
          }
        }
      }
      dialogueBlock = null;
    }

    // Process current line
    if (!dialogueBlock) {
      if (shouldStartNewPage(currentHeight, line, i)) {
        // If we're breaking after a scene heading, move it to the next page
        if (sceneStartIndex != null && sceneStartIndex === currentPage.length - 1) {
          const sceneHeading = currentPage.pop();
          const sceneH = getHeight(sceneOriginalIndex);
          currentHeight -= sceneH;
          if (currentPage.length) pages.push([...currentPage]);
          currentPage = [sceneHeading, line];
          currentHeight = sceneH + h;
        } else {
          if (currentPage.length) pages.push([...currentPage]);
          currentPage = [line];
          currentHeight = h;
        }
        sceneStartIndex = line.type === 'scene' ? 0 : null;
      } else {
        currentPage.push(line);
        currentHeight += h;
      }
    }
  }

  // Handle any remaining dialogue block
  if (dialogueBlock) {
    const remaining = contentAreaPx - currentHeight;
    if (dialogueBlock.totalHeight <= remaining || currentPage.length === 0) {
      currentPage.push(...dialogueBlock.elements);
    } else {
      // Split remaining dialogue block with (MORE)/(CONT'D)
      const charH = getHeight(dialogueBlock.characterIdx);
      const moreH = charH;
      const minForSplit = charH + 19 + moreH;
      if (remaining < minForSplit) {
        pages.push([...currentPage]);
        currentPage = [...dialogueBlock.elements];
      } else {
        const placed = [dialogueBlock.elements[0]];
        let usedH = charH;
        for (let di = 1; di < dialogueBlock.elements.length; di++) {
          const elH = getHeight(dialogueBlock.indices[di]);
          if (usedH + elH + moreH > remaining) break;
          placed.push(dialogueBlock.elements[di]);
          usedH += elH;
        }
        placed.push({
          key: `more-${dialogueBlock.characterIdx}`,
          type: 'character',
          text: '(MORE)',
          isMore: true
        });
        currentPage.push(...placed);
        pages.push([...currentPage]);

        const characterName = dialogueBlock.character.text.replace(/\s*\([^)]*\)$/, '').trim();
        const existingExt = dialogueBlock.character.extension || '';
        const contdExt = existingExt ? ` ${existingExt} (CONT'D)` : " (CONT'D)";
        currentPage = [{
          ...dialogueBlock.character,
          key: `contd-${dialogueBlock.characterIdx}`,
          text: characterName + contdExt,
          isContinued: true
        }];
        const placedCount = placed.length - 2;
        for (let di = 1 + placedCount; di < dialogueBlock.elements.length; di++) {
          currentPage.push(dialogueBlock.elements[di]);
        }
      }
    }
  }

  // Add any remaining content
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
};

const formatContentToPages = (content) => {
  const scriptContent = normalizeNewlines(content)
    // Strip boneyard blocks (/* ... */) — Fountain spec: hidden from output
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const lines = scriptContent.split('\n');
  const formatted = [];
  let previousElement = null;
  let inDualDialogue = false;

  // Process each line
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    const upperLine = trimmedLine.toUpperCase();
    let elementType;
    let isForced = false;
    let extension = '';
    let isDual = false;

    // Skip empty lines but add them to maintain spacing
    if (!trimmedLine) {
      formatted.push({ key: i, type: 'blank', text: '' });
      // Do NOT reset inDualDialogue on blank lines — the blank between
      // the first dialogue block and the ^-character is normal Fountain spacing.
      continue;
    }

    // Check for forced page break (=== or ***)
    if (/^={3,}$/.test(trimmedLine) || /^\*{3,}$/.test(trimmedLine)) {
      formatted.push({ key: i, type: 'pageBreak', text: '' });
      continue;
    }

    // Sections (# ## ###) — kept as structural markers for the Scene Navigator,
    // but not rendered as visible content (no spacing added in the spaced loop).
    if (/^#{1,6}\s/.test(trimmedLine)) {
      const level = (trimmedLine.match(/^#+/) || [''])[0].length;
      const sectionText = trimmedLine.replace(/^#+\s*/, '').trim();
      formatted.push({ key: i, type: 'section', text: sectionText, level });
      continue;
    }

    // Synopses (= prefix, but not === page break which is already handled above)
    // Kept for the Scene Navigator, not rendered as visible content.
    if (/^=(?!=)/.test(trimmedLine)) {
      const synopsisText = trimmedLine.replace(/^=\s*/, '').trim();
      formatted.push({ key: i, type: 'synopsis', text: synopsisText });
      continue;
    }

    // Check for dual dialogue marker at end
    if (trimmedLine.endsWith('^')) {
      isDual = true;
      inDualDialogue = true;
    }

    // Context checks for unforced character cues. A short all-caps line in the
    // middle of an action paragraph (e.g. "THE DOOR SLAMS") should NOT become
    // a character cue unless it's followed by dialogue/parenthetical or is
    // preceded by a blank line (or is forced with @).
    const prevLine = i > 0 ? lines[i - 1].trim() : '';
    const nextLine = i < lines.length - 1 ? lines[i + 1].trim() : '';
    const hasBlankBefore = i === 0 || prevLine === '';
    const hasDialogueAfter = nextLine !== '' &&
      !/^(INT\.|EXT\.|INT\/EXT\.|INT\.\/EXT\.|I\/E\.|EST\.)/i.test(nextLine) &&
      !TRANSITIONS.some(t => nextLine.toUpperCase().startsWith(t));

    // Check for Fountain syntax markers
    if (trimmedLine.startsWith('!')) {
      isForced = true;
      elementType = 'action';
    } else if (trimmedLine.startsWith('.') && /^\.[a-zA-Z0-9]/.test(trimmedLine)) {
      // Forced scene heading: period followed by alphanumeric (not ellipsis like ...)
      isForced = true;
      elementType = 'scene';
    } else if (trimmedLine.startsWith('@')) {
      isForced = true;
      elementType = 'character';
    } else if (trimmedLine.startsWith('>') && trimmedLine.endsWith('<')) {
      // Centered text (not transition) - must both start with > and end with <
      isForced = true;
      elementType = 'centered';
    } else if ((trimmedLine.startsWith('>') || trimmedLine.endsWith('<')) && !trimmedLine.endsWith('>')) {
      // Transition - starts with > or ends with <, but NOT both (that's centered)
      isForced = true;
      elementType = 'transition';
    } else if (trimmedLine.startsWith('~')) {
      isForced = true;
      elementType = 'lyric';
    } else if (trimmedLine.startsWith('[[')) {
      elementType = 'note';
    } else {
      // Check for scene headers. Strip surrounding emphasis markers first so a
      // bolded/italicised slugline (e.g. **INT. OFFICE - DAY**) is still
      // recognised as a scene heading instead of being demoted to action.
      const upperNoEmphasis = upperLine.replace(/^[_*]+/, '');
      const isSceneHeader = SCENE_PREFIXES.some(prefix => 
        upperLine.startsWith(prefix) || upperLine.startsWith('.' + prefix) ||
        upperNoEmphasis.startsWith(prefix) || upperNoEmphasis.startsWith('.' + prefix)
      );
      
      if (isSceneHeader) {
        elementType = 'scene';
        inDualDialogue = false; // Reset dual dialogue on new scene
      }
      // Check for transitions
      else if (TRANSITIONS.includes(upperLine) || 
               (upperLine.endsWith('TO:') && upperLine === upperLine.toUpperCase())) {
        elementType = 'transition';
        inDualDialogue = false;
      }
      // Check for character names with extensions (improved regex)
      // Character names: 2-30 chars, ALL CAPS, can include spaces, hyphens, apostrophes
      // The optional trailing ` ^` is the Fountain dual dialogue marker — allow it here.
      // Require context: blank line before, or dialogue-like content after, otherwise
      // short all-caps action lines (e.g. "THE DOOR SLAMS") get misclassified.
      else if ((hasBlankBefore || hasDialogueAfter) &&
               trimmedLine.replace(/\s*\^$/, '').match(/^[A-Z][A-Z0-9\s\-\']{1,28}(\([^)]*\))?$/) && 
               !SCENE_PREFIXES.some(prefix => upperLine.replace(/\s*\^$/, '').toUpperCase().startsWith(prefix)) &&
               !TRANSITIONS.some(t => upperLine.replace(/\s*\^$/, '').toUpperCase().startsWith(t)) &&
               !upperLine.startsWith('FADE') &&
               !upperLine.startsWith('CUT') &&
               !upperLine.replace(/\s*\^$/, '').toUpperCase().endsWith('TO')) {
        const match = trimmedLine.match(/^([A-Z][A-Z0-9\s\-\']*?)(\([^)]*\))?\s*\^?$/);
        if (match) {
          const [_, name, ext] = match;
          elementType = 'character';
          if (ext) {
            extension = ext.trim();
          }
        }
      }
      // Check for parentheticals
      else if (trimmedLine.match(/^\([^)]*\)$/)) {
        elementType = 'parenthetical';
      }
      // Dialogue follows character names or parentheticals (or in dual dialogue block)
      else if (previousElement === 'character' || previousElement === 'parenthetical' || inDualDialogue) {
        elementType = 'dialogue';
      }
      // Everything else is action — and end any dual dialogue state
      else {
        elementType = 'action';
        inDualDialogue = false;
      }
    }

    // After a dialogue line that was part of inDualDialogue, check if the NEXT
    // non-blank line would continue dialogue or not. We end dual state when we
    // see something that clearly isn't a continuation (handled by action branch above).
    // However if we just set dialogue AND we were inDualDialogue, keep the flag
    // so the REST of the right-column block is captured. Reset only on action.
    if (elementType === 'dialogue' && inDualDialogue) {
      // stay in dual — do nothing, flag remains
    } else if (elementType !== 'character' && elementType !== 'dialogue' &&
               elementType !== 'parenthetical' && elementType !== 'lyric') {
      inDualDialogue = false;
    }

    // Clean up the text based on Fountain syntax
    // Extract scene numbers (#1A#) before stripping them — for dual-sided rendering
    let sceneNumber = '';
    if (elementType === 'scene') {
      const sceneNumMatch = trimmedLine.match(/#([a-zA-Z0-9.\-]+)#\s*$/);
      if (sceneNumMatch) sceneNumber = sceneNumMatch[1];
    }
    let cleanedText = trimmedLine
      .replace(/^[!@>~]/, '')     // Remove Fountain force characters
      .replace(/^\.(?=[a-zA-Z0-9])/, '') // Remove leading forced-scene dot (only before alphanumeric)
      .replace(/\[\[[^\]]*\]\]/g, '') // Remove inline notes (global)
      .replace(/^>|<$/g, '')      // Remove transition/centered markers
      .replace(/\s*\^$/, '')     // Remove dual dialogue marker from text (with optional space)
      .replace(/#([a-zA-Z0-9.\-]+)#\s*$/, '') // Remove scene numbers (#1A#)
      .trim();

    // Special handling for character names
    if (elementType === 'character') {
      let characterName = cleanedText;
      if (extension) {
        // Standardize the extension against the known list
        extension = standardizeExtension(extension);
        characterName = characterName.replace(/\([^)]*\)/, '').trim();
      }
      cleanedText = characterName.toUpperCase() + (extension ? ' ' + extension : '');
    }

    // Special handling for scene headers
    if (elementType === 'scene') {
      cleanedText = cleanedText.toUpperCase();
    }

    // Special handling for transitions
    if (elementType === 'transition') {
      cleanedText = cleanedText.toUpperCase();
      // Only append " TO:" if the text is a single word that isn't already a
      // known transition. Multi-word transitions (e.g. "BACK TO SCENE") or
      // anything containing FADE/CUT/DISSOLVE/BLACK should be left as-is.
      const isSingleWord = !cleanedText.includes(' ');
      const isKnownTransition = TRANSITIONS.includes(cleanedText) ||
        cleanedText.endsWith('TO:') || cleanedText.includes('FADE') ||
        cleanedText.includes('CUT') || cleanedText.includes('DISSOLVE') ||
        cleanedText.includes('BLACK');
      if (isSingleWord && !isKnownTransition) {
        cleanedText += ' TO:';
      }
    }

    formatted.push({
      key: i,
      type: elementType,
      text: cleanedText,
      original: trimmedLine,
      isForced,
      isDual,
      extension: elementType === 'character' ? extension : undefined,
      sceneNumber: elementType === 'scene' ? sceneNumber : undefined
    });

    previousElement = elementType;
  }

  // Apply standard screenplay spacing rules (like Fade In):
  // Remove all raw blank lines, then insert semantic spacing based on element transitions.
  const nonBlank = formatted.filter(el => el.type !== 'blank');
  const spaced = [];
  let blankCounter = 0;
  const addBlank = () => {
    spaced.push({ key: `sp-${blankCounter++}`, type: 'blank', text: '' });
  };

  for (let idx = 0; idx < nonBlank.length; idx++) {
    const el = nonBlank[idx];
    const prev = idx > 0 ? nonBlank[idx - 1] : null;

    if (idx === 0) {
      // No blank before the very first element
      spaced.push(el);
      continue;
    }

    // Page breaks and structural markers (sections/synopses) pass through without spacing
    if (el.type === 'pageBreak' || el.type === 'section' || el.type === 'synopsis') {
      spaced.push(el);
      continue;
    }

    // Standard screenplay spacing rules:
    // - No blank between character → dialogue/parenthetical/lyric
    // - No blank between parenthetical → dialogue/lyric
    // - No blank between dialogue → parenthetical
    // - No blank between lyric → lyric (consecutive lyrics)
    // - isDual character always gets a blank before it (it starts the right column block)
    const dialogueTypes = ['dialogue', 'lyric'];
    const isDialogueContinuation =
      !el.isDual && (
        (dialogueTypes.includes(el.type) && (prev.type === 'character' || prev.type === 'parenthetical')) ||
        (el.type === 'parenthetical' && (prev.type === 'character' || dialogueTypes.includes(prev.type))) ||
        (el.type === 'lyric' && dialogueTypes.includes(prev.type))
      );

    if (isDialogueContinuation) {
      // No blank line within a dialogue block
      spaced.push(el);
    } else {
      // 1 blank line before all other elements (scene, action, character, transition, etc.)
      addBlank();
      spaced.push(el);
    }
  }

  // Group dual dialogue: find the second ^-marked character block and pair it with the
  // preceding character block to form a single compound dualDialogue element.
  const grouped = [];
  let i2 = 0;
  while (i2 < spaced.length) {
    const el = spaced[i2];
    // Look for a character line flagged isDual — that marks the RIGHT column
    if (el.type === 'character' && el.isDual) {
      // Collect RIGHT column: character + dialogue/parenthetical that follow
      const right = [];
      right.push(el);
      let j = i2 + 1;
      while (j < spaced.length && ['dialogue', 'parenthetical', 'lyric'].includes(spaced[j].type)) {
        right.push(spaced[j]);
        j++;
      }

      // Walk backwards in grouped[] to find the preceding character block (LEFT column)
      let leftStart = grouped.length - 1;
      // skip trailing blank
      while (leftStart >= 0 && grouped[leftStart].type === 'blank') leftStart--;
      // collect dialogue/parenthetical/lyric going up
      const leftLines = [];
      while (leftStart >= 0 && ['dialogue', 'parenthetical', 'lyric'].includes(grouped[leftStart].type)) {
        leftLines.unshift(grouped[leftStart]);
        leftStart--;
      }
      // leftStart should now point to the LEFT character line
      if (leftStart >= 0 && grouped[leftStart].type === 'character') {
        const leftChar = grouped[leftStart];
        // Remove the left block (char + its dialogue) from grouped, also any trailing blank
        const removeCount = grouped.length - leftStart;
        grouped.splice(leftStart, removeCount);
        // Also remove the blank that was added before the left character
        if (grouped.length > 0 && grouped[grouped.length - 1].type === 'blank') {
          grouped.pop();
        }
        const left = [leftChar, ...leftLines];
        grouped.push({
          key: `dual-${leftChar.key}-${el.key}`,
          type: 'dualDialogue',
          left,
          right,
          text: ''
        });
      } else {
        // No preceding left block found — just push as-is
        for (const r of right) grouped.push(r);
      }
      i2 = j;
    } else {
      grouped.push(el);
      i2++;
    }
  }

  // Measure actual rendered heights via hidden DOM container, then paginate
  const heights = measureElementHeights(grouped);
  const pages = calculatePages(grouped, heights);

  return pages;
};

// Extract title page info (Fountain metadata first, fallback to pre-slugline block)
const extractTitlePage = (rawContent = '') => {
  const normalized = normalizeNewlines(rawContent);
  const lines = normalized.split('\n');

  const meta = {
    title: '',
    credit: '',
    author: '',
    source: '',
    draft: '',
    copyright: '',
    contact: ''
  };

  let bodyStartIndex = 0;
  let foundMeta = false;
  let lastMetaIndex = -1;

  const metaRegex = /^(title|credit|author|source|draft date|revision|copyright|contact):\s*(.*)$/i;
  const knownMetaKeys = new Set(['title', 'credit', 'author', 'source', 'draft date', 'revision', 'copyright', 'contact']);
  const sceneHeaderRegex = /^\.|^(INT\.|EXT\.|I\/E\.|EST\.|INT\/EXT\.|INT\/ EXT\.|INT\/EXT\.|I\/E\.|INT\/EXT|MONTAGE|SERIES OF SHOTS|BEGIN MONTAGE|END MONTAGE)/i;
  const isBodyStartLine = (text = '') => {
    const trimmed = text.trim();
    if (!trimmed) return false;
    return sceneHeaderRegex.test(trimmed) ||
      trimmed.startsWith('!') ||
      trimmed.startsWith('@') ||
      trimmed.startsWith('>') ||
      trimmed.startsWith('~') ||
      isTransitionLine(trimmed);
  };

  // Pass 1: Fountain-style metadata at top
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      bodyStartIndex = i + 1;
      continue;
    }

    const metaMatch = line.match(metaRegex);
    if (metaMatch) {
      const key = metaMatch[1].toLowerCase();
      if (!knownMetaKeys.has(key)) {
        break;
      }
      foundMeta = true;
      const value = metaMatch[2].trim();
      lastMetaIndex = i;
      let continuationLines = [];
      let lookahead = i + 1;
      while (lookahead < lines.length) {
        const nextLine = lines[lookahead];
        const trimmedNext = nextLine.trim();
        if (!trimmedNext) break;
        if (metaRegex.test(trimmedNext) || isBodyStartLine(trimmedNext)) {
          break;
        }
        if (/^\s+/.test(nextLine)) {
          continuationLines.push(trimmedNext);
          bodyStartIndex = lookahead + 1;
          lookahead += 1;
          continue;
        }
        break;
      }
      const combinedValue = [value, ...continuationLines].filter(Boolean).join('\n').trim();
      if (key === 'title') meta.title = combinedValue || meta.title;
      if (key === 'credit') meta.credit = combinedValue || meta.credit;
      if (key === 'author') meta.author = combinedValue || meta.author;
      if (key === 'source') meta.source = combinedValue || meta.source;
      if (key === 'draft date' || key === 'revision') meta.draft = combinedValue || meta.draft;
      if (key === 'copyright') meta.copyright = combinedValue || meta.copyright;
      if (key === 'contact') meta.contact = combinedValue || meta.contact;
      bodyStartIndex = i + 1;
      if (lookahead > i + 1) {
        bodyStartIndex = lookahead;
        i = lookahead - 1;
        lastMetaIndex = i;
      }
      continue;
    }

    // Stop meta scan once we hit something that looks like the script body
    if (isBodyStartLine(line)) {
      break;
    }
  }

  if (!foundMeta) {
    return {
      titlePage: null,
      bodyContent: normalized
    };
  }

  const tentativeBodyStart = Math.max(bodyStartIndex, lastMetaIndex + 1);
  let firstBodyIndex = tentativeBodyStart;
  while (firstBodyIndex < lines.length) {
    const candidate = lines[firstBodyIndex].trim();
    if (!candidate) {
      firstBodyIndex += 1;
      continue;
    }
    if (metaRegex.test(candidate)) {
      const key = candidate.match(metaRegex)[1].toLowerCase();
      if (knownMetaKeys.has(key)) {
        firstBodyIndex += 1;
        continue;
      }
    }
    break;
  }
  bodyStartIndex = Math.min(firstBodyIndex, lines.length);

  const hasTitlePage = meta.title || meta.credit || meta.author || meta.source || meta.draft || meta.contact || meta.copyright;
  const bodyContent = lines.slice(bodyStartIndex).join('\n');

  return {
    titlePage: hasTitlePage ? meta : null,
    bodyContent
  };
};

// ─── Pure helper functions (hoisted outside component to avoid re-creation) ───

// Strip Fountain emphasis markers from text to get plain display text
const stripEmphasisMarkers = (text) => {
  if (!text) return '';
  return text
    .replace(/\\\*/g, '\u0000').replace(/\\_/g, '\u0001')
    .replace(/\*{3}(.+?)\*{3}/g, '$1')
    .replace(/\*{2}(.+?)\*{2}/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/\u0000/g, '*').replace(/\u0001/g, '_');
};

// Map plain-text offset to source offset (accounting for emphasis markers)
const plainToSourceOffset = (sourceLine, plainOffset) => {
  let srcPos = 0;
  let plainPos = 0;
  while (srcPos < sourceLine.length && plainPos < plainOffset) {
    let markerLen = 0;
    if (sourceLine.slice(srcPos, srcPos + 3) === '***') markerLen = 3;
    else if (sourceLine.slice(srcPos, srcPos + 2) === '**') markerLen = 2;
    else if (sourceLine[srcPos] === '*') markerLen = 1;
    else if (sourceLine[srcPos] === '_') markerLen = 1;
    if (markerLen > 0) {
      srcPos += markerLen;
    } else {
      srcPos++;
      plainPos++;
    }
  }
  return srcPos;
};

// Map plain-text segments back to source, preserving existing emphasis markers
const mapPlainToSource = (sourceLine, beforePlain, targetPlain, afterPlain, prefix, suffix) => {
  const srcStart = plainToSourceOffset(sourceLine, beforePlain.length);
  const srcEnd = plainToSourceOffset(sourceLine, beforePlain.length + targetPlain.length);
  return sourceLine.substring(0, srcStart)
    + prefix
    + sourceLine.substring(srcStart, srcEnd)
    + suffix
    + sourceLine.substring(srcEnd);
};

// Get the true cursor offset from the start of a contentEditable element
// (works correctly even when the element contains multiple text nodes / emphasis spans)
const getCursorOffsetInElement = (el) => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  try {
    const range = sel.getRangeAt(0);
    const preRange = document.createRange();
    preRange.selectNodeContents(el);
    preRange.setEnd(range.startContainer, range.startOffset);
    return preRange.toString().length;
  } catch (err) {
    return 0;
  }
};

// Set cursor at a character offset inside a contentEditable element
// (walks text nodes to find the right position)
const setCursorAtOffset = (el, targetOffset) => {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
  let offset = 0;
  let node;
  while ((node = walker.nextNode())) {
    const nodeLen = node.textContent.length;
    if (offset + nodeLen >= targetOffset) {
      const range = document.createRange();
      range.setStart(node, targetOffset - offset);
      range.collapse(true);
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(range);
      return;
    }
    offset += nodeLen;
  }
  // Fallback: place at end
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const s = window.getSelection();
  s.removeAllRanges();
  s.addRange(range);
};

// Get the source-key integer from a DOM element or its closest ancestor
// Skips blank lines (not editable) so dragging across scene/action lines works
const getSourceKeyFromEl = (el) => {
  let target = el?.closest?.('[data-source-key]');
  while (target && target.dataset.lineType === 'blank') {
    target = target.parentElement?.closest?.('[data-source-key]');
  }
  if (!target) return null;
  const k = parseInt(target.dataset.sourceKey);
  return isNaN(k) ? null : k;
};

// Memoized editable line — skips React re-render while focused so the browser's
// contentEditable cursor position is never disturbed by DOM reconciliation.
const EditableLine = React.memo(({ index, sourceKey, lineType, isLocked, style, onInput, onFocus, onBlur, onMouseUp, isFocused, children }) => {
  const divRef = useRef(null);

  // Imperatively sync DOM content when not focused.
  // React's suppressContentEditableWarning means React doesn't fully manage
  // the DOM inside contentEditable, so we need to manually update it.
  useLayoutEffect(() => {
    const el = divRef.current;
    if (!el || isFocused) return;
    
    if (typeof children === 'string') {
      // Plain text - set directly.
      // Also force a reset when the DOM still holds element children (e.g.
      // leftover tag-highlight spans). In that case el.textContent equals the
      // plain string, so a textContent check alone would skip clearing the spans.
      if (el.textContent !== children || el.childElementCount > 0) {
        el.textContent = children;
      }
    } else if (Array.isArray(children)) {
      // JSX children (emphasis spans) - convert to HTML string and use innerHTML
      // This avoids React reconciliation conflicts with manual DOM manipulation
      const renderChildrenToHtml = (items) => {
        return items.map(child => {
          if (typeof child === 'string') {
            // Escape HTML special characters
            return child.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          } else if (child && child.type === 'span' && child.props) {
            const style = child.props.style || {};
            const styleStr = Object.entries(style)
              .map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v}`)
              .join('; ');
            const inner = child.props.children;
            let innerHtml = '';
            if (typeof inner === 'string') {
              innerHtml = inner.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            } else if (Array.isArray(inner)) {
              innerHtml = renderChildrenToHtml(inner);
            }
            return `<span style="${styleStr}">${innerHtml}</span>`;
          }
          return '';
        }).join('');
      };
      const html = renderChildrenToHtml(children);
      if (el.innerHTML !== html) {
        el.innerHTML = html;
      }
    }
  }, [children, isFocused]);

  return (
    <div
      ref={divRef}
      key={index}
      data-line-number={index}
      data-source-key={sourceKey}
      data-line-type={lineType}
      contentEditable={!isLocked}
      suppressContentEditableWarning
      spellCheck={!isLocked}
      onInput={onInput}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseUp={onMouseUp}
      style={style}
    >
      {/* Don't render JSX children here - useLayoutEffect manages DOM manually */}
    </div>
  );
}, (prevProps, nextProps) => {
  // While the line IS focused, skip ALL re-renders to protect cursor position.
  // When focus is lost (prev=focused, next=not), allow re-render to restore emphasis.
  if (nextProps.isFocused) return true;
  // Otherwise, re-render if any visible props changed
  return (
    prevProps.sourceKey === nextProps.sourceKey &&
    prevProps.lineType === nextProps.lineType &&
    prevProps.isLocked === nextProps.isLocked &&
    prevProps.style === nextProps.style &&
    prevProps.children === nextProps.children &&
    prevProps.isFocused === nextProps.isFocused
  );
});

const SimpleScreenplayViewer = React.forwardRef(({
  content,
  coverPage = null,
  onBack,
  onUpdateTags,
  scriptId,
  projectId,
  initialTags = [],
  disabledTagging = false,
  scriptTitle = 'Script',
  showPrintButton = false,
  onPrint,
  onDownload,
  onAnalyze,
  canAnalyze = true,
  analyzeLoading = false,
  analysisStatus = null
}, ref) => {
  const [selectedText, setSelectedText] = useState(null);
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);
  const [tags, setTags] = useState(initialTags);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);
  const [isAutoTagging, setIsAutoTagging] = useState(false);
  const [tagRemovalConfirm, setTagRemovalConfirm] = useState(null);
  const muiTheme = useMuiTheme();
  const isDarkMode = muiTheme.palette.mode === 'dark';
  const [editableContent, setEditableContent] = useState(content);
  const [pages, setPages] = useState([]);
  const [titlePage, setTitlePage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [focusedLineKey, setFocusedLineKey] = useState(null);
  const pagesContainerRef = useRef(null);
  const rePaginateTimerRef = useRef(null);
  const focusedLineKeyRef = useRef(null);
  const pendingRepaginateRef = useRef(false);
  const pendingFocusKeyRef = useRef(null);
  const dualFocusMoveRef = useRef(false);
  const wrapSelectionWithRef = useRef(null);
  const insertSnippetAtCursorRef = useRef(null);
  const lastFocusedLineKeyRef = useRef(null);
  const suppressNextInputRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const deferredEditTimerRef = useRef(null);
  const latestEditableContentRef = useRef(editableContent);
  const capturedSelectionRef = useRef(null);
  const [transitionMenuAnchor, setTransitionMenuAnchor] = useState(null);
  const [characterMenuAnchor, setCharacterMenuAnchor] = useState(null);
  const [scenePrefixAnchor, setScenePrefixAnchor] = useState(null);
  const [locationMenuAnchor, setLocationMenuAnchor] = useState(null);
  const [timeMenuAnchor, setTimeMenuAnchor] = useState(null);
  const [shortcutAnchor, setShortcutAnchor] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [acTick, setAcTick] = useState(0);
  const autocompleteRef = useRef(null);
  const autocompleteStateRef = useRef({ visible: false, items: [], index: 0, context: null, pos: null });
  const [sceneHeadings, setSceneHeadings] = useState([]);
  const [sceneSearch, setSceneSearch] = useState('');
  const [activeSceneKey, setActiveSceneKey] = useState(null);
  // Update autocomplete ref + bump tick to re-render the dropdown.
  // The dropdown reads from the ref; acTick just forces a re-render.
  const setAutocomplete = useCallback((valOrFn) => {
    const prev = autocompleteStateRef.current;
    const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
    if (next === prev) return;
    autocompleteStateRef.current = next;
    setAcTick(t => t + 1);
  }, []);
  const acLocationsRef = useRef([]);
  const acTimesRef = useRef([]);
  const acCharactersRef = useRef([]);
  const editableLinesRef = useRef([]);

  // Multi-line selection state: { startKey, endKey } where keys are source line indices.
  // null means no multi-line selection is active.
  const [multiLineSelection, setMultiLineSelection] = useState(null);
  const multiLineSelectionRef = useRef(null);
  // Track mouse drag across lines
  const dragAnchorKeyRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Print method exposed via ref
  const printScreenplay = useCallback(() => {
    // Create a dedicated print container at body level
    let printContainer = document.getElementById('screenplay-dedicated-print');
    if (!printContainer) {
      printContainer = document.createElement('div');
      printContainer.id = 'screenplay-dedicated-print';
      printContainer.style.cssText = `
        position: absolute;
        left: 0;
        top: 0;
        width: 8.5in;
        background: white;
        font-family: 'Courier Prime', Courier, monospace;
      `;
      document.body.appendChild(printContainer);
    }
    
    // Clone the print area content
    const printArea = document.getElementById('screenplay-print-area');
    if (printArea) {
      printContainer.innerHTML = printArea.innerHTML;
      
      // Add print-specific styles
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        @page { size: 8.5in 11in; margin: 0; }
        #screenplay-dedicated-print .script-page {
          display: block;
          page-break-after: always;
          break-after: page;
          width: 8.5in;
          height: 11in;
          box-sizing: border-box;
          padding: 1in 1in 1in 1.5in;
          background: white;
          overflow: hidden;
        }
        #screenplay-dedicated-print * {
          background: transparent !important;
          color: #000 !important;
          box-shadow: none !important;
        }
      `;
      printContainer.appendChild(styleEl);
      
      // Print
      window.print();
      
      // Cleanup after print
      setTimeout(() => {
        printContainer.remove();
      }, 1000);
    } else {
      window.print();
    }
  }, []);

  // Expose print method via ref
  React.useImperativeHandle(ref, () => ({
    printScreenplay
  }), [printScreenplay]);

  // Ensure Courier Prime is loaded (used throughout the screenplay editor)
  useEffect(() => {
    const id = 'courier-prime-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Courier+Prime&display=swap';
      document.head.appendChild(link);
    }
  }, []);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyRef = useRef(history);
  const historyIndexRef = useRef(-1);
  const isApplyingHistoryRef = useRef(false);
  const HISTORY_LIMIT = 200;
  const metadataTimerRef = useRef(null);

  // Debounced character name and scene metadata extraction (300ms after last edit)
  const [characterNames, setCharacterNames] = useState(() => extractCharacterNames(content));
  const [sceneMetadata, setSceneMetadata] = useState(() => extractSceneMetadata(content));
  const filteredScenes = useMemo(() => {
    const term = sceneSearch.trim().toUpperCase();
    if (!term) return sceneHeadings;
    return sceneHeadings.filter(scene => (scene.text || '').toUpperCase().includes(term));
  }, [sceneHeadings, sceneSearch]);
  useEffect(() => {
    if (metadataTimerRef.current) clearTimeout(metadataTimerRef.current);
    metadataTimerRef.current = setTimeout(() => {
      setCharacterNames(extractCharacterNames(editableContent));
      setSceneMetadata(extractSceneMetadata(editableContent));
    }, 300);
    return () => clearTimeout(metadataTimerRef.current);
  }, [editableContent]);
  const timeOfDayOptions = useMemo(() => {
    const set = new Set(TIME_OF_DAY_OPTIONS.map(opt => opt.toUpperCase()));
    sceneMetadata.times.forEach(time => set.add(time.toUpperCase()));
    return Array.from(set).sort();
  }, [sceneMetadata]);

  const keepAutocompleteDataInSync = () => {
    acLocationsRef.current = sceneMetadata.locations;
    acTimesRef.current = timeOfDayOptions;
    acCharactersRef.current = characterNames;
  };

  // Keep autocomplete refs fresh when metadata updates
  useEffect(() => {
    keepAutocompleteDataInSync();
  }, [sceneMetadata, timeOfDayOptions, characterNames]);

  // Refresh cached list of editable lines after content updates
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      editableLinesRef.current = Array.from(document.querySelectorAll('[data-source-key]')).filter(el => el.isContentEditable);
    });
    return () => cancelAnimationFrame(rafId);
  }, [editableContent]);

  const addTitlePageTemplate = () => {
    const existingContent = editableContent || '';
    const normalized = normalizeNewlines(existingContent);
    const trimmedStart = normalized.trimStart();
    if (/^title:/i.test(trimmedStart)) {
      return;
    }
    const combined = `${TITLE_PAGE_TEMPLATE}\n\n${trimmedStart}`.trimEnd();
    const formatted = autoFormatScreenplay(combined);
    setEditableContent(formatted);
    setHasUnsavedChanges(true);
    if (!isApplyingHistoryRef.current) {
      pushHistory(formatted);
    }
  };

  const resetHistory = useCallback((content) => {
    const initial = content ? [content] : [];
    setHistory(initial);
    const nextIndex = initial.length ? 0 : -1;
    setHistoryIndex(nextIndex);
    historyIndexRef.current = nextIndex;
  }, []);

  useEffect(() => {
    latestEditableContentRef.current = editableContent;
  }, [editableContent]);

  useEffect(() => {
    setIsLocked(true);
  }, [scriptId]);

  // Auto-focus first editable line when unlocking a new/blank script
  useEffect(() => {
    if (isLocked) return;
    // Small delay to let DOM render after unlock
    const timer = setTimeout(() => {
      const firstAction = document.querySelector('[data-line-type="action"][data-source-key]');
      if (firstAction && firstAction.isContentEditable) {
        firstAction.focus();
        setCursorAtOffset(firstAction, 0);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isLocked]);

  useEffect(() => {
    if (!isLocked) {
      resetHistory(latestEditableContentRef.current);
    } else {
      resetHistory('');
    }
  }, [isLocked, resetHistory]);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  const pushHistory = useCallback((content) => {
    if (isLocked) return;
    setHistory(prev => {
      const currentIndex = historyIndexRef.current;
      const truncated = currentIndex >= 0 ? prev.slice(0, currentIndex + 1) : [];
      if (truncated[truncated.length - 1] === content) {
        return truncated.length === prev.length ? prev : truncated;
      }
      const next = [...truncated, content].slice(-HISTORY_LIMIT);
      const newIndex = next.length - 1;
      historyIndexRef.current = newIndex;
      setHistoryIndex(newIndex);
      return next;
    });
  }, [isLocked]);

  // Keep editable content in sync with prop updates (e.g. fresh uploads)
  useEffect(() => {
    const normalizedContent = normalizeNewlines(content || '');
    const coverText = coverPage ? coverPageToText(coverPage) : '';
    const combined = coverText ? `${coverText}\n\n${normalizedContent}`.trimEnd() : normalizedContent;
    setEditableContent(combined);
  }, [content, coverPage]);

  // Save changes to backend
  const saveChanges = useCallback(async (updatedContent) => {
    if (!scriptId || !projectId) return;
    try {
      setIsSaving(true);
      setSaveError(null);
      await api.put(`/scripts/${scriptId}`, {
        content: updatedContent,
        projectId
      });
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save script:', err);
      setSaveError('Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [scriptId, projectId]);

  // Re-paginate from current editableContent (stable — reads from ref)
  const extractSceneList = useCallback((pagesArray = []) => {
    const scenes = [];
    pagesArray.forEach((pageLines, pageIndex) => {
      pageLines.forEach((line) => {
        if (line.type === 'scene' || line.type === 'section' || line.type === 'synopsis') {
          scenes.push({
            key: line.key,
            text: line.text,
            page: pageIndex + 1,
            type: line.type,
            level: line.level || 0
          });
        }
      });
    });
    return scenes;
  }, []);

  const doPaginate = useCallback(() => {
    const current = latestEditableContentRef.current;
    if (!current) return;
    const { titlePage: tp, bodyContent } = extractTitlePage(current);
    setTitlePage(tp);
    if (bodyContent) {
      const newPages = formatContentToPages(bodyContent);
      setPages(newPages);
      setSceneHeadings(extractSceneList(newPages));
    } else {
      setPages([]);
      setSceneHeadings([]);
    }
    pendingRepaginateRef.current = false;
  }, [extractSceneList]);

  // After repagination, fire any pending focus request
  useLayoutEffect(() => {
    const key = pendingFocusKeyRef.current;
    if (key == null) return;
    pendingFocusKeyRef.current = null;
    const el = pagesContainerRef.current?.querySelector(`[data-source-key="${key}"]`);
    if (el && el.isContentEditable) {
      el.focus();
      setCursorAtOffset(el, 0);
    }
  }, [pages]);

  // Auto-save with debouncing — reads from ref so it saves during typing
  // even though editableContent state only updates on blur
  useEffect(() => {
    if (hasUnsavedChanges) {
      const timeoutId = setTimeout(() => {
        saveChanges(latestEditableContentRef.current);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [hasUnsavedChanges, saveChanges]);

  // Re-paginate whenever editableContent changes (debounced to avoid thrashing during typing)
  useEffect(() => {
    if (rePaginateTimerRef.current) clearTimeout(rePaginateTimerRef.current);
    // If a line is focused, defer pagination until blur
    if (focusedLineKeyRef.current != null) {
      pendingRepaginateRef.current = true;
      return () => { /* cleanup — timer already cleared above */ };
    }
    rePaginateTimerRef.current = setTimeout(() => {
      doPaginate();
    }, 150);
    return () => clearTimeout(rePaginateTimerRef.current);
  }, [editableContent, doPaginate]);

  // Handle text selection
  const handleTextSelection = (event) => {
    if (disabledTagging || !showTags) return;
    const selection = window.getSelection();
    const selectedStr = selection.toString().trim();
    
    if (selectedStr) {
      const lineElement = event.target.closest('[data-line-number]');
      if (!lineElement) return;
      
      const lineNumber = parseInt(lineElement.dataset.lineNumber);
      
      setSelectedText({
        text: selectedStr,
        lineNumber: lineNumber
      });
      
      setTagMenuAnchor(event.target);
    }
  };

  // Handle tag creation
  const handleCreateTag = async (tagType) => {
    if (disabledTagging || !showTags) return;
    if (selectedText) {
      // Check if this exact text segment already has this tag type
      const existingTag = tags.find(tag => 
        tag.type === tagType && 
        tag.text === selectedText.text &&
        tag.lineNumber === selectedText.lineNumber
      );

      if (!existingTag) {
        // Handle CAST tags as CHARACTER
        const finalTagType = tagType === 'CAST' ? 'CHARACTER' : tagType;
        // Get tag info with fallback for unknown tag types
        const tagInfo = TAG_TYPES[finalTagType] || { label: finalTagType, color: '#9575CD' };
        
        const newTag = {
          id: Date.now(),
          type: finalTagType,
          text: selectedText.text,
          lineNumber: selectedText.lineNumber,
          color: tagInfo.color
        };
        
        const updatedTags = [...tags, newTag];
        setTags(updatedTags);
        
        // Save tags to backend
        if (onUpdateTags) {
          try {
            await onUpdateTags(updatedTags);
          } catch (error) {
            console.error('Failed to save tags:', error);
          }
        }
      }
      
      // Close the menu after applying the tag
      setTagMenuAnchor(null);
      setSelectedText(null);
    }
  };

  // Handle tag removal
  const handleRemoveTag = async (tagId) => {
    if (disabledTagging || !showTags) return;
    const updatedTags = tags.filter(tag => tag.id !== tagId);
    setTags(updatedTags);
    
    // Save tags to backend
    if (onUpdateTags) {
      try {
        await onUpdateTags(updatedTags);
      } catch (error) {
        console.error('Failed to save tags:', error);
      }
    }
  };

  // Handle auto-tagging
  const handleAutoTag = async () => {
    if (disabledTagging || !showTags) return;
    try {
      setIsAutoTagging(true);
      setSaveError(null);
      const response = await autoTagScript(scriptId, projectId);
      if (response.tags) {
        setTags(response.tags);
        if (onUpdateTags) {
          onUpdateTags(response.tags);
        }
      }
    } catch (error) {
      console.error('Failed to auto-tag script:', error);
      setSaveError('Auto-tag failed');
    } finally {
      setIsAutoTagging(false);
    }
  };

  // Note: cursor restore via useLayoutEffect was removed — EditableLine React.memo
  // now prevents React from touching the focused line's DOM, so the browser cursor
  // is never disturbed and no restore is needed.

  // Handle inline line edit — maps a changed line back to the Fountain source
  const handleLineInput = useCallback((lineKey, e) => {
    if (suppressNextInputRef.current) {
      suppressNextInputRef.current = false;
      return;
    }

    // Strip zero-width space (used as dialogue placeholder in dual dialogue columns)
    const newText = (e.currentTarget.innerText || '').replace(/\u200B/g, '');
    const currentContent = latestEditableContentRef.current;
    if (!currentContent) return;

    const { bodyContent } = extractTitlePage(currentContent);
    const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
    const sourceLines = normalizeNewlines(bodyContent).split('\n');

    // lineKey is the original source line index — replace that line.
    // Scene headings and transitions are always uppercased in Fountain source.
    const lineType = e.currentTarget.dataset?.lineType || '';

    // Live scene detection: a fresh line isn't reclassified to 'scene' until
    // blur/Enter, so detect a slugline from the typed text (emphasis-tolerant)
    // to uppercase it immediately as the user types.
    const sceneProbe = newText.trim().toUpperCase().replace(/^[_*]+/, '');
    const looksLikeScene = SCENE_PREFIXES.some(prefix =>
      sceneProbe.startsWith(prefix) || sceneProbe.startsWith('.' + prefix)
    );

    // Apply uppercase visually without touching textContent (keeps the caret).
    // Don't clear it on element types that already uppercase via their base style
    // (scene/character/transition) to avoid overriding that styling.
    const baseUppercases = lineType === 'scene' || lineType === 'character' || lineType === 'transition';
    if (looksLikeScene) {
      e.currentTarget.style.textTransform = 'uppercase';
    } else if (!baseUppercases) {
      e.currentTarget.style.textTransform = '';
    }

    const shouldUppercase = lineType === 'scene' || lineType === 'transition' || looksLikeScene;
    if (typeof lineKey === 'number' && lineKey >= 0 && lineKey < sourceLines.length) {
      let savedText = shouldUppercase ? newText.toUpperCase() : newText;
      // If this source line is the right-column ^-character, preserve the ^ marker
      // so the dual dialogue block survives repagination after editing the name.
      const existingSource = sourceLines[lineKey] || '';
      if (lineType === 'character' && existingSource.trimEnd().endsWith('^')) {
        savedText = savedText.replace(/\s*\^$/, '').trimEnd() + ' ^';
      }
      // Parenthetical lines must stay wrapped in () in source so the parser
      // recognises them. Strip any user-typed parens then re-wrap.
      if (lineType === 'parenthetical') {
        const inner = savedText.replace(/^\(|\)$/g, '').trim();
        savedText = inner ? `(${inner})` : '(\u200B)';
      }
      sourceLines[lineKey] = savedText;
    }

    const updatedContent = titlePart + sourceLines.join('\n');
    // Update the ref immediately so other logic (autocomplete, save) sees fresh content.
    // Do NOT call setEditableContent here — that would trigger a React re-render which
    // replaces the contentEditable's DOM children and resets the browser cursor position.
    latestEditableContentRef.current = updatedContent;
    hasUnsavedChangesRef.current = true;
    if (!isApplyingHistoryRef.current) {
      pushHistory(updatedContent);
    }
    // Do NOT call setEditableContent during typing — React state syncs on blur only.
    // The ref (latestEditableContentRef) is already updated above for autocomplete/save.

    const capturedText = newText;
    const capturedKey = lineKey;

    // Defer autocomplete check so it survives React re-render
    setTimeout(() => {
      // Re-query the line element for fresh position (element may have been replaced by React)
      const freshEl = document.querySelector(`[data-source-key="${capturedKey}"]`);
      let caretTop = 0;
      let caretLeft = 0;
      if (freshEl) {
        const rect = freshEl.getBoundingClientRect();
        caretTop = rect.bottom + 4;
        caretLeft = rect.left;
      }

      try {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const r = sel.getRangeAt(0).getBoundingClientRect();
          if (r.bottom > 0) { caretTop = r.bottom + 4; caretLeft = r.left; }
        }
      } catch (_) {}

      const upper = capturedText.toUpperCase().trim();

      // 1) Scene prefix shorthand
      if (AC_PREFIX_RE.test(upper)) {
        const trimmedUpper = upper.replace(/\s+$/, '');
        const items = SCENE_PREFIXES.filter(p => p.toUpperCase().startsWith(trimmedUpper));
        if (items.length > 0) {
          setAutocomplete({ visible: true, items, index: 0, context: 'prefix', pos: { top: caretTop, left: caretLeft }, lineKey: capturedKey });
          return;
        }
      }

      // 2) After scene prefix + space → locations
      const pm = upper.match(AC_LOCATION_RE);
      if (pm && !upper.includes(' - ')) {
        const typed = pm[2].trim();
        const locs = acLocationsRef.current.length > 0 ? acLocationsRef.current : ['LOCATION'];
        const items = typed ? locs.filter(l => l.toUpperCase().includes(typed.toUpperCase())) : locs;
        if (items.length > 0) {
          setAutocomplete({ visible: true, items, index: 0, context: 'location', pos: { top: caretTop, left: caretLeft }, lineKey: capturedKey });
          return;
        }
      }

      // 3) After location + " - " → time of day
      const tm = upper.match(AC_TIME_RE);
      if (tm) {
        const typed = tm[1].trim();
        const times = acTimesRef.current;
        const items = typed ? times.filter(t => t.toUpperCase().includes(typed.toUpperCase())) : times;
        if (items.length > 0) {
          setAutocomplete({ visible: true, items, index: 0, context: 'time', pos: { top: caretTop, left: caretLeft }, lineKey: capturedKey });
          return;
        }
      }

      // 4) ALL-CAPS → character names (allowed inside dual columns too — ^ preserved by applyAutocompleteSuggestion)
      const trimmedText = capturedText.trim();
      if (trimmedText.length >= 2 && AC_CHARACTER_RE.test(trimmedText)) {
        const typed = trimmedText.toUpperCase();
        const items = acCharactersRef.current.filter(c => c.toUpperCase().includes(typed) && c.toUpperCase() !== typed);
        if (items.length > 0) {
          setAutocomplete({ visible: true, items, index: 0, context: 'character', pos: { top: caretTop, left: caretLeft }, lineKey: capturedKey });
          return;
        }
      }

      // No match — hide
      setAutocomplete(prev => prev.visible ? { visible: false, items: [], index: 0, context: null, pos: null } : prev);
    }, 50);
  }, [pushHistory]);

  // Apply the selected autocomplete suggestion (stable — reads from refs, no state deps)
  const applyAutocompleteSuggestion = useCallback((suggestion) => {
    const { context, lineKey } = autocompleteStateRef.current;
    const lineEl = document.querySelector(`[data-source-key="${lineKey}"]`);
    if (!lineEl) { setAutocomplete({ visible: false, items: [], index: 0, context: null, pos: null }); return; }

    const currentText = lineEl.innerText || '';
    let newText = currentText;

    if (context === 'prefix') {
      newText = suggestion + ' ';
    } else if (context === 'location') {
      // Match against currentText directly (regex is case-insensitive) so the
      // m[0]/m[2] lengths index into the same string we slice from.
      const m = currentText.match(AC_LOCATION_RE);
      if (m) {
        newText = currentText.substring(0, m[0].length - m[2].length) + suggestion;
      }
    } else if (context === 'time') {
      // AC_TIME_RE triggers the dropdown as soon as a whitespace-preceded dash
      // exists (trailing space optional), e.g. "INT. OFFICE -". Target the last
      // dash directly so selection works without requiring an extra space.
      const dashIdx = currentText.lastIndexOf('-');
      if (dashIdx !== -1) {
        newText = currentText.substring(0, dashIdx).trimEnd() + ' - ' + suggestion;
      }
    } else if (context === 'character') {
      newText = suggestion;
    }

    // Update the DOM and commit to source
    lineEl.innerText = newText;
    const currentContent = latestEditableContentRef.current;
    if (currentContent) {
      const { bodyContent } = extractTitlePage(currentContent);
      const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
      const sourceLines = normalizeNewlines(bodyContent).split('\n');
      if (typeof lineKey === 'number' && lineKey >= 0 && lineKey < sourceLines.length) {
        let savedText = newText;
        // Preserve ^ marker on right-column character lines
        const existingSource = sourceLines[lineKey] || '';
        if (existingSource.trimEnd().endsWith('^')) {
          savedText = savedText.replace(/\s*\^$/, '').trimEnd() + ' ^';
        }
        sourceLines[lineKey] = savedText;
      }
      const updated = titlePart + sourceLines.join('\n');
      latestEditableContentRef.current = updated;
      setEditableContent(updated);
      setHasUnsavedChanges(true);
      if (!isApplyingHistoryRef.current) pushHistory(updated);
    }

    // Hide dropdown and refocus with cursor at end
    setAutocomplete({ visible: false, items: [], index: 0, context: null, pos: null });
    setTimeout(() => {
      lineEl.focus();
      const range = document.createRange();
      const textNode = lineEl.firstChild || lineEl;
      try {
        range.setStart(textNode, textNode.textContent?.length || 0);
        range.collapse(true);
        const s = window.getSelection();
        s.removeAllRanges();
        s.addRange(range);
      } catch (err) { /* best-effort */ }
    }, 30);
  }, [pushHistory]);

  // Helper: find the editable line element from the current selection or active element
  const getActiveLineEl = useCallback(() => {
    const activeEl = document.activeElement;
    if (activeEl?.dataset?.sourceKey !== undefined) return activeEl;
    if (activeEl?.closest) {
      const lineEl = activeEl.closest('[data-source-key]');
      if (lineEl) return lineEl;
    }
    // Fallback: when focus moved to toolbar button, use last focused line
    const lastKey = lastFocusedLineKeyRef.current;
    if (lastKey != null) {
      const el = document.querySelector(`[data-source-key="${lastKey}"]`);
      if (el) return el;
    }
    return null;
  }, []);

  // stripEmphasisMarkers is hoisted outside the component (pure function)

  // Helper: get source line key and source lines for editing (stable — reads from ref)
  const getSourceContext = useCallback(() => {
    const current = latestEditableContentRef.current;
    if (!current) return null;
    const { bodyContent } = extractTitlePage(current);
    const titlePart = current.substring(0, current.length - bodyContent.length);
    const sourceLines = normalizeNewlines(bodyContent).split('\n');
    return { titlePart, sourceLines };
  }, []);

  // Helper: commit updated source lines back to editableContent
  const commitSourceLines = useCallback((titlePart, sourceLines) => {
    const updatedBody = sourceLines.join('\n');
    const updated = titlePart + updatedBody;
    // Keep the ref in sync so handleLineInput always reads fresh content
    latestEditableContentRef.current = updated;
    setEditableContent(updated);
    setHasUnsavedChanges(true);
    if (!isApplyingHistoryRef.current) pushHistory(updated);
  }, [pushHistory]);

  // Append text to the end of the current/last focused source line (same line, not new line)
  const appendToCurrentLine = useCallback((text) => {
    const lineEl = getActiveLineEl();
    const ctx = getSourceContext();
    if (!ctx) return;
    const { titlePart, sourceLines } = ctx;

    let lineKey = lineEl ? parseInt(lineEl.dataset.sourceKey) : lastFocusedLineKeyRef.current;
    if (lineKey == null || isNaN(lineKey) || lineKey < 0 || lineKey >= sourceLines.length) {
      // No focused line — append as new line at end
      sourceLines.push(text);
      focusedLineKeyRef.current = null;
      pendingRepaginateRef.current = false;
      commitSourceLines(titlePart, sourceLines);
      return;
    }

    // Append to the end of the current line
    sourceLines[lineKey] = sourceLines[lineKey] + text;
    focusedLineKeyRef.current = null;
    pendingRepaginateRef.current = false;
    commitSourceLines(titlePart, sourceLines);

    // Re-focus the line after re-render
    setTimeout(() => {
      const el = pagesContainerRef.current?.querySelector(`[data-source-key="${lineKey}"]`);
      if (el) {
        el.focus();
        try {
          const range = document.createRange();
          const textNode = el.firstChild || el;
          range.setStart(textNode, textNode.textContent?.length || 0);
          range.collapse(true);
          const s = window.getSelection();
          s.removeAllRanges();
          s.addRange(range);
        } catch (err) { /* best-effort */ }
      }
    }, 250);
  }, [getActiveLineEl, getSourceContext, commitSourceLines]);

  // Insert a snippet at the cursor position in the source
  const insertSnippetAtCursor = useCallback((snippet) => {
    const lineEl = getActiveLineEl();
    const ctx = getSourceContext();
    if (!ctx) return;
    const { titlePart, sourceLines } = ctx;

    // Split multi-line snippets into separate source lines
    const snippetLines = snippet.split('\n');

    // Determine which source line to insert after
    let lineKey = lineEl ? parseInt(lineEl.dataset.sourceKey) : lastFocusedLineKeyRef.current;
    if (lineKey == null || isNaN(lineKey) || lineKey < 0 || lineKey >= sourceLines.length) {
      // No focused line — append to end
      sourceLines.push(...snippetLines);
      commitSourceLines(titlePart, sourceLines);
      focusedLineKeyRef.current = null;
      pendingRepaginateRef.current = false;
      return;
    }

    // Insert snippet lines after the current line
    sourceLines.splice(lineKey + 1, 0, ...snippetLines);
    focusedLineKeyRef.current = null;
    pendingRepaginateRef.current = false;
    commitSourceLines(titlePart, sourceLines);

    // Focus the FIRST non-empty inserted line (left column character name)
    // Use pendingFocusKeyRef so focus fires after repagination DOM is ready
    let targetKey = lineKey + 1;
    for (let i = 0; i < snippetLines.length; i++) {
      if (snippetLines[i].trim()) { targetKey = lineKey + 1 + i; break; }
    }
    pendingFocusKeyRef.current = targetKey;
  }, [getActiveLineEl, getSourceContext, commitSourceLines]);
  insertSnippetAtCursorRef.current = insertSnippetAtCursor;

  // Convert the current line's Fountain type (used by toolbar element buttons)
  const convertLineType = useCallback((targetType) => {
    const lineEl = getActiveLineEl();
    const ctx = getSourceContext();
    if (!ctx) return;
    const { titlePart, sourceLines } = ctx;

    let lineKey = lineEl ? parseInt(lineEl.dataset.sourceKey) : lastFocusedLineKeyRef.current;
    if (lineKey == null || isNaN(lineKey) || lineKey < 0 || lineKey >= sourceLines.length) return;

    let currentLine = sourceLines[lineKey];
    // Strip existing Fountain forced-type prefixes (!, ., @, >, ~)
    let stripped = currentLine.replace(/^[!.@>~]\s*/, '').trim();
    // Also strip known scene prefixes if converting away from scene.
    // Pick the LONGEST matching prefix so compound forms (e.g. INT./EXT.) are
    // fully removed rather than leaving a fragment like "/EXT.".
    const upperStripped = stripped.toUpperCase();
    let matchedPrefix = '';
    for (const prefix of SCENE_PREFIXES) {
      if (upperStripped.startsWith(prefix) && prefix.length > matchedPrefix.length) {
        matchedPrefix = prefix;
      }
    }
    if (matchedPrefix) {
      stripped = stripped.substring(matchedPrefix.length).trim();
    }
    // Strip centered markers >...<
    stripped = stripped.replace(/^>\s*/, '').replace(/\s*<$/, '').trim();
    // Strip transition suffixes
    stripped = stripped.replace(/\s*TO:$/i, '').trim();

    // Apply new type prefix
    let newLine = stripped;
    switch (targetType) {
      case 'scene':
        newLine = 'INT. ' + (stripped || 'LOCATION') + ' - DAY';
        break;
      case 'action':
        newLine = stripped || 'Action description here.';
        break;
      case 'character':
        newLine = stripped ? stripped.toUpperCase() : '@';
        break;
      case 'dialogue':
        newLine = stripped || 'Dialogue goes here.';
        break;
      case 'parenthetical':
        newLine = '(' + (stripped || 'parenthetical') + ')';
        break;
      case 'transition':
        newLine = '>' + (stripped ? stripped.toUpperCase() : 'CUT') + ' TO:';
        break;
      case 'centered':
        newLine = '>' + (stripped || 'THE END') + '<';
        break;
      case 'dualDialogue': {
        // If cursor is on a character line, toggle the ^ dual marker
        const upper = currentLine.trim().toUpperCase();
        const isCharLine = /^[A-Z@][A-Z0-9\s\-\']{0,28}(\([^)]*\))?\^?$/.test(currentLine.trim()) &&
          !SCENE_PREFIXES.some(p => upper.startsWith(p));
        if (isCharLine) {
          // Toggle the ^ marker
          if (currentLine.trim().endsWith('^')) {
            newLine = currentLine.trim().replace(/\s*\^$/, '');
          } else {
            newLine = currentLine.trim() + ' ^';
          }
          sourceLines[lineKey] = newLine;
          focusedLineKeyRef.current = null;
          pendingRepaginateRef.current = false;
          commitSourceLines(titlePart, sourceLines);
          setTimeout(() => {
            const el = document.querySelector(`[data-source-key="${lineKey}"]`);
            if (el && el.isContentEditable) { el.focus(); setCursorAtOffset(el, el.innerText?.length || 0); }
          }, 300);
          return;
        }
        // Not on a character line — insert full template snippet
        insertSnippetAtCursorRef.current?.(QUICK_INSERT_SNIPPETS.dualDialogue);
        return;
      }
      case 'lyric':
        newLine = '~' + (stripped || 'Lyric line here');
        break;
      case 'section':
        newLine = '# ' + (stripped || 'Act One');
        break;
      case 'synopsis':
        newLine = '= ' + (stripped || 'Brief description of this scene.');
        break;
      default:
        newLine = stripped;
    }

    sourceLines[lineKey] = newLine;
    focusedLineKeyRef.current = null;
    pendingRepaginateRef.current = false;
    pendingFocusKeyRef.current = lineKey;
    commitSourceLines(titlePart, sourceLines);
  }, [getActiveLineEl, getSourceContext, commitSourceLines]);

  // plainToSourceOffset and mapPlainToSource are hoisted outside the component (pure functions)

  // Wrap selected text with emphasis markers in the Fountain source (WYSIWYG)
  const wrapSelectionWith = useCallback((prefix, suffix, captured = null) => {
    // Use captured selection if provided (from mousedown), otherwise get current selection
    let lineEl, selectedText, selStart, selEnd, domText;
    
    if (captured && captured.text && captured.lineEl) {
      lineEl = captured.lineEl;
      selectedText = captured.text;
      // Calculate offset from the captured range
      const range = captured.range;
      const preRange = document.createRange();
      preRange.selectNodeContents(lineEl);
      preRange.setEnd(range.startContainer, range.startOffset);
      selStart = preRange.toString().length;
      selEnd = selStart + selectedText.length;
      domText = lineEl.innerText || '';
    } else {
      lineEl = getActiveLineEl();
      if (!lineEl) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      selectedText = selection.toString();
      if (!selectedText) return;
      const range = selection.getRangeAt(0);
      const preRange = document.createRange();
      preRange.selectNodeContents(lineEl);
      preRange.setEnd(range.startContainer, range.startOffset);
      selStart = preRange.toString().length;
      selEnd = selStart + selectedText.length;
      domText = lineEl.innerText || '';
    }

    const ctx = getSourceContext();
    if (!ctx) return;
    const { titlePart, sourceLines } = ctx;

    const lineKey = parseInt(lineEl.dataset.sourceKey);
    if (isNaN(lineKey) || lineKey < 0 || lineKey >= sourceLines.length) return;

    const sourceLine = sourceLines[lineKey];

    if (selectedText) {
      // Build new line with markers wrapped around selection
      // But we must preserve existing emphasis markers in the source
      const before = domText.substring(0, selStart);
      const target = domText.substring(selStart, selEnd);
      const after = domText.substring(selEnd);

      // Map the plain-text positions back to source positions to preserve existing markers
      const srcStart = plainToSourceOffset(sourceLine, before.length);
      const srcEnd = plainToSourceOffset(sourceLine, before.length + target.length);

      // Check if already wrapped with the same markers - if so, unwrap (toggle off)
      const textBeforeSelection = sourceLine.substring(0, srcStart);
      const textAfterSelection = sourceLine.substring(srcEnd);
      const selectedSourceText = sourceLine.substring(srcStart, srcEnd);

      // Check if the selection is inside existing formatting markers
      // Strategy: Find all formatted regions, then check overlap
      
      let formattedRegions = [];
      
      // Simple manual scanning for markers
      const markerLen = prefix.length;
      let pos = 0;

      while (pos < sourceLine.length) {
        // Find opening marker
        const openPos = sourceLine.indexOf(prefix, pos);
        if (openPos === -1) break;
        
        // For single * (italic), skip if it's part of ** (bold)
        if (prefix === '*') {
          const charBefore = openPos > 0 ? sourceLine[openPos - 1] : '';
          const charAfter = sourceLine[openPos + 1] || '';
          if (charBefore === '*' || charAfter === '*') {
            pos = openPos + 1;
            continue;
          }
        }
        
        // Find closing marker
        const contentStart = openPos + markerLen;
        const closePos = sourceLine.indexOf(suffix, contentStart);
        if (closePos === -1) break;
        
        // For single * (italic), skip if closing is part of **
        if (suffix === '*') {
          const charBefore = closePos > 0 ? sourceLine[closePos - 1] : '';
          const charAfter = sourceLine[closePos + 1] || '';
          if (charBefore === '*' || charAfter === '*') {
            pos = closePos + 1;
            continue;
          }
        }
        
        const innerText = sourceLine.substring(contentStart, closePos);
        const sourceStart = openPos;
        const sourceEnd = closePos + markerLen;
        
        // Calculate plain-text position by stripping markers from text before this match
        const textBefore = sourceLine.substring(0, sourceStart);
        const plainBefore = stripEmphasisMarkers(textBefore);
        const plainStart = plainBefore.length;
        // innerText may contain nested markers, so strip them for accurate plain length
        const plainInnerText = stripEmphasisMarkers(innerText);
        const plainEnd = plainStart + plainInnerText.length;
        
        formattedRegions.push({
          sourceStart,
          sourceEnd,
          plainStart,
          plainEnd,
          innerText,
          fullMatch: sourceLine.substring(sourceStart, sourceEnd)
        });
        
        pos = sourceEnd;
      }

      // Check if the selection overlaps with any formatted region
      const selPlainStart = before.length;
      const selPlainEnd = before.length + target.length;
      
      // Find a region that contains or overlaps with the selection
      const overlappingRegion = formattedRegions.find(region => {
        return selPlainStart < region.plainEnd && selPlainEnd > region.plainStart;
      });
      
      let newLine;
      if (overlappingRegion) {
        // Toggle OFF: Remove the markers from this region
        newLine = sourceLine.substring(0, overlappingRegion.sourceStart) 
          + overlappingRegion.innerText 
          + sourceLine.substring(overlappingRegion.sourceEnd);
      } else {
        // Toggle ON: add the markers
        newLine = mapPlainToSource(sourceLine, before, target, after, prefix, suffix);
      }

      sourceLines[lineKey] = newLine;
    } else {
      // No selection — insert placeholder markers at cursor position
      // Get cursor offset in the plain text
      const sel = window.getSelection();
      const range = sel.getRangeAt(0);
      // Use a temporary range to measure offset from start of line
      const preRange = document.createRange();
      preRange.selectNodeContents(lineEl);
      preRange.setEnd(range.startContainer, range.startOffset);
      const cursorOffset = preRange.toString().length;

      // Get source line for this lineKey
      const sourceLine = sourceLines[lineKey];
      const plainSource = stripEmphasisMarkers(sourceLine);

      // Map cursor offset to source position (simplified: insert at end if can't map)
      const plainLen = plainSource.length;
      const ratio = sourceLine.length / (plainLen || 1);
      const approxSrcPos = Math.min(Math.round(cursorOffset * ratio), sourceLine.length);
      const before = sourceLine.substring(0, approxSrcPos);
      const after = sourceLine.substring(approxSrcPos);
      sourceLines[lineKey] = `${before}${prefix}text${suffix}${after}`;
    }

    // Clear focus so EditableLine memo allows re-render with emphasis spans
    focusedLineKeyRef.current = null;
    setFocusedLineKey(null);
    pendingRepaginateRef.current = false;
    commitSourceLines(titlePart, sourceLines);

    // Force DOM update on the line element immediately (since memo may skip re-render)
    // This ensures the visual display matches the source even if the line gets re-focused
    const updatedLine = sourceLines[lineKey];
    setTimeout(() => {
      const el = pagesContainerRef.current?.querySelector(`[data-source-key="${lineKey}"]`);
      if (el) {
        // Always update DOM to plain text (emphasis will be rendered when unfocused)
        // This ensures toggle-off visually removes bold/italic immediately
        const plainText = stripEmphasisMarkers(updatedLine);
        el.textContent = plainText;
        el.focus();
      }
    }, 50);
  }, [getActiveLineEl, getSourceContext, commitSourceLines]);
  wrapSelectionWithRef.current = wrapSelectionWith;

  // getCursorOffsetInElement and setCursorAtOffset are hoisted outside the component (pure functions)

  // Native DOM keydown listener — bypasses React synthetic events for reliability
  // Reads from refs to stay stable and avoid re-attaching on every keystroke
  useEffect(() => {
    const handler = (e) => {
      // Skip all keyboard handling when screenplay is locked
      if (isLocked) return;

      // --- Undo/Redo (Ctrl+Z, Ctrl+Y, Ctrl+Shift+Z) ---
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          // Undo
          const idx = historyIndexRef.current;
          if (idx > 0) {
            const prevContent = historyRef.current[idx - 1];
            isApplyingHistoryRef.current = true;
            setEditableContent(prevContent);
            latestEditableContentRef.current = prevContent;
            setHistoryIndex(idx - 1);
            historyIndexRef.current = idx - 1;
            setHasUnsavedChanges(true);
            setTimeout(() => { isApplyingHistoryRef.current = false; }, 0);
          }
          return;
        }
        if ((e.key === 'y') || (e.key === 'z' && e.shiftKey)) {
          e.preventDefault();
          // Redo
          const idx = historyIndexRef.current;
          if (idx < historyRef.current.length - 1) {
            const nextContent = historyRef.current[idx + 1];
            isApplyingHistoryRef.current = true;
            setEditableContent(nextContent);
            latestEditableContentRef.current = nextContent;
            setHistoryIndex(idx + 1);
            historyIndexRef.current = idx + 1;
            setHasUnsavedChanges(true);
            setTimeout(() => { isApplyingHistoryRef.current = false; }, 0);
          }
          return;
        }
      }

      // --- Autocomplete keyboard navigation (read from ref for fresh state) ---
      const ac = autocompleteStateRef.current;
      if (ac.visible) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setAutocomplete(prev => ({ ...prev, index: Math.min(prev.index + 1, prev.items.length - 1) }));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setAutocomplete(prev => ({ ...prev, index: Math.max(prev.index - 1, 0) }));
          return;
        }
        if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey)) {
          e.preventDefault();
          if (ac.items[ac.index]) {
            applyAutocompleteSuggestion(ac.items[ac.index]);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setAutocomplete({ visible: false, items: [], index: 0, context: null, pos: null });
          return;
        }
      }

      // Find the closest editable line element
      // e.target may be a text node inside contentEditable, which has no .closest()
      const targetEl = e.target.nodeType === 3 ? e.target.parentElement : e.target;
      const lineEl = targetEl?.closest?.('[data-source-key]');
      if (!lineEl || !lineEl.isContentEditable) return;
      const sourceKey = lineEl.dataset.sourceKey;
      const lineKey = parseInt(sourceKey);

      // Smart dual dialogue handling — Enter navigates between existing lines
      const dualContainer = lineEl.closest('[data-line-type="dualDialogue"]');
      if (dualContainer) {
        const currentLineType = lineEl.dataset.lineType || 'action';

        // Collect all editable source keys in this dual block, sorted
        const allColEls = Array.from(dualContainer.querySelectorAll('[data-source-key]'))
          .filter(el => el.isContentEditable);
        const allColKeys = allColEls
          .map(el => parseInt(el.dataset.sourceKey))
          .filter(k => !isNaN(k))
          .sort((a, b) => a - b);

        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
          e.preventDefault();

          const myIdx = allColKeys.indexOf(lineKey);

          // Helper: focus helper that sets dualFocusMoveRef
          const focusKey = (k) => {
            dualFocusMoveRef.current = true;
            setTimeout(() => {
              const el = document.querySelector(`[data-source-key="${k}"]`);
              if (el && el.isContentEditable) { el.focus(); setCursorAtOffset(el, 0); }
            }, 0);
          };

          if (currentLineType === 'character') {
            // Character → check if next line is already parenthetical (navigate) or insert new
            const nextKey = myIdx + 1 < allColKeys.length ? allColKeys[myIdx + 1] : null;
            if (nextKey !== null) {
              const nextEl = document.querySelector(`[data-source-key="${nextKey}"]`);
              if (nextEl && nextEl.dataset.lineType === 'parenthetical') {
                // Parenthetical exists, navigate to it
                focusKey(nextKey);
                return;
              }
            }
            // No parenthetical exists → insert NEW one (like normal mode)
            const currentContent = latestEditableContentRef.current;
            if (!currentContent) return;
            const { bodyContent } = extractTitlePage(currentContent);
            const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
            const sourceLines = normalizeNewlines(bodyContent).split('\n');
            const rawText = lineEl.innerText || '';
            if (lineKey >= 0 && lineKey < sourceLines.length) {
              // Preserve dual-dialogue marker (^) since the DOM display strips it.
              const existingSource = sourceLines[lineKey] || '';
              if (existingSource.trimEnd().endsWith('^')) {
                sourceLines[lineKey] = rawText.replace(/\s*\^$/, '').trimEnd() + ' ^';
              } else {
                sourceLines[lineKey] = rawText;
              }
            }
            const insertAt = lineKey + 1;
            sourceLines.splice(insertAt, 0, '()');
            const updated = titlePart + sourceLines.join('\n');
            latestEditableContentRef.current = updated;
            focusedLineKeyRef.current = null;
            setFocusedLineKey(null);
            pendingRepaginateRef.current = false;
            setEditableContent(updated);
            setHasUnsavedChanges(true);
            if (!isApplyingHistoryRef.current) pushHistory(updated);
            // Focus the new parenthetical with cursor BETWEEN the parens (offset 1) like normal mode
            const poll = (n) => {
              if (n <= 0) return;
              const el = document.querySelector(`[data-source-key="${insertAt}"]`);
              if (el && el.isContentEditable) {
                el.focus();
                setCursorAtOffset(el, 1); // Cursor between the parens
              } else {
                setTimeout(() => poll(n - 1), 50);
              }
            };
            setTimeout(() => poll(20), 50);
            return;
          }

          if (currentLineType === 'parenthetical') {
            const trimmedText = (lineEl.innerText || '').trim();
            const isEmpty = trimmedText === '()' || trimmedText === '(' || trimmedText === '' || trimmedText === '\u200B';

            if (isEmpty) {
              // Empty parenthetical → convert to dialogue in-place (no re-render)
              lineEl.textContent = '';
              lineEl.dataset.lineType = 'dialogue';
              // Scale down dialogue margins for dual column
              lineEl.style.margin = '';
              lineEl.style.marginLeft = `${MEASUREMENTS.DIALOGUE_INDENT / 2}in`;
              lineEl.style.marginRight = '0';
              lineEl.style.marginTop = '0';
              lineEl.style.marginBottom = '0';
              lineEl.style.textTransform = 'none';
              // Update source to dialogue placeholder
              const currentContent = latestEditableContentRef.current;
              if (currentContent) {
                const { bodyContent } = extractTitlePage(currentContent);
                const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
                const sourceLines = normalizeNewlines(bodyContent).split('\n');
                if (lineKey >= 0 && lineKey < sourceLines.length) {
                  sourceLines[lineKey] = '\u200B';
                  latestEditableContentRef.current = titlePart + sourceLines.join('\n');
                  hasUnsavedChangesRef.current = true;
                  if (!isApplyingHistoryRef.current) pushHistory(latestEditableContentRef.current);
                }
              }
              lineEl.focus();
              setCursorAtOffset(lineEl, 0);
              return;
            } else {
              // Filled parenthetical → move to dialogue line
              let nextDialogueKey = null;
              for (let ni = myIdx + 1; ni < allColKeys.length; ni++) {
                const candidate = document.querySelector(`[data-source-key="${allColKeys[ni]}"]`);
                if (candidate && candidate.dataset.lineType === 'dialogue') { nextDialogueKey = allColKeys[ni]; break; }
                if (candidate && candidate.dataset.lineType === 'character') break;
              }
              if (nextDialogueKey !== null) { focusKey(nextDialogueKey); }
              return;
            }
          }

          if (currentLineType === 'dialogue') {
            const isLastLine = allColKeys.length > 0 && lineKey === allColKeys[allColKeys.length - 1];
            if (isLastLine) {
              // Find the next editable element AFTER the dual dialogue container
              const nextEditable = (() => {
                const allEditables = Array.from(document.querySelectorAll('[data-source-key]'))
                  .filter(el => el.isContentEditable);
                const currentIdx = allEditables.findIndex(el => el === lineEl);
                return currentIdx >= 0 && currentIdx + 1 < allEditables.length
                  ? allEditables[currentIdx + 1]
                  : null;
              })();
              
              if (nextEditable) {
                dualFocusMoveRef.current = true;
                setTimeout(() => {
                  nextEditable.focus();
                  setCursorAtOffset(nextEditable, 0);
                }, 0);
              } else {
                // At end of document - insert an action line to continue
                const currentContent = latestEditableContentRef.current;
                if (currentContent) {
                  const { bodyContent } = extractTitlePage(currentContent);
                  const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
                  const sourceLines = normalizeNewlines(bodyContent).split('\n');
                  const insertAt = allColKeys[allColKeys.length - 1] + 1;
                  sourceLines.splice(insertAt, 0, '', '!');
                  const updated = titlePart + sourceLines.join('\n');
                  latestEditableContentRef.current = updated;
                  setEditableContent(updated);
                  setHasUnsavedChanges(true);
                  if (!isApplyingHistoryRef.current) pushHistory(updated);
                  
                  // Poll for the new action line and focus it
                  const targetKey = insertAt + 1;
                  const poll = (n) => {
                    if (n <= 0) return;
                    const el = document.querySelector(`[data-source-key="${targetKey}"]`);
                    if (el && el.isContentEditable) {
                      el.focus();
                      setCursorAtOffset(el, 0);
                    } else {
                      setTimeout(() => poll(n - 1), 50);
                    }
                  };
                  setTimeout(() => poll(20), 50);
                }
              }
              return;
            }
            // Left-column dialogue Enter: move to right-column character line
            let nextCharKey = null;
            for (let ni = myIdx + 1; ni < allColKeys.length; ni++) {
              const candidate = document.querySelector(`[data-source-key="${allColKeys[ni]}"]`);
              if (candidate && candidate.dataset.lineType === 'character') { nextCharKey = allColKeys[ni]; break; }
            }
            if (nextCharKey !== null) { focusKey(nextCharKey); }
            return;
          }
        }

        if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey) {
          e.preventDefault();
          // Tab: move to next existing line in the block
          const myIdx = allColKeys.indexOf(lineKey);
          const nextKey = myIdx >= 0 && myIdx + 1 < allColKeys.length
            ? allColKeys[myIdx + 1]
            : null;
          if (nextKey !== null) {
            dualFocusMoveRef.current = true;
            setTimeout(() => {
              const el = document.querySelector(`[data-source-key="${nextKey}"]`);
              if (el && el.isContentEditable) { el.focus(); setCursorAtOffset(el, 0); }
            }, 0);
          }
          return;
        }

        // All other keys inside dual dialogue: pass through to browser default
        return;
      }

      // Tab — move cursor to character position with empty line, show character dropdown
      if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault();
        // Clear focus so EditableLine memo allows re-render with character indent styling
        focusedLineKeyRef.current = null;
        setFocusedLineKey(null);
        lastFocusedLineKeyRef.current = lineKey;
        // Convert to character type (uses @ prefix for empty lines)
        convertLineType('character');
        // After convertLineType re-renders and refocuses (~300ms), show character dropdown
        const chars = acCharactersRef.current;
        if (chars.length > 0) {
          setTimeout(() => {
            const freshEl = document.querySelector(`[data-source-key="${lineKey}"]`);
            if (!freshEl) return;
            const rect = freshEl.getBoundingClientRect();
            setAutocomplete({
              visible: true,
              items: chars,
              index: 0,
              context: 'character',
              pos: { top: rect.bottom + 4, left: rect.left },
              lineKey
            });
          }, 350);
        }
        return;
      }

      // Enter — context-aware auto-flow for screenplay writing
      // Scene/Action/Transition → blank + action
      // Character → parenthetical "()" with cursor between parens
      // Empty parenthetical → dialogue (synchronous DOM mutation)
      // Parenthetical with content → dialogue (zero-width space placeholder)
      // Dialogue → blank + action (exits dialogue block)
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault();
        const currentLineType = lineEl.dataset.lineType || 'action';
        const currentContent = latestEditableContentRef.current;
        if (!currentContent) return;

        // Flush any pending deferred edit first
        if (deferredEditTimerRef.current) {
          clearTimeout(deferredEditTimerRef.current);
          deferredEditTimerRef.current = null;
        }
        // Sync ref with current DOM text.
        // Scene headings and transitions must be uppercased in source.
        const rawText = lineEl.innerText || '';
        const shouldUppercaseOnEnter = currentLineType === 'scene' || currentLineType === 'transition';
        const currentText = shouldUppercaseOnEnter ? rawText.toUpperCase() : rawText;
        const { bodyContent } = extractTitlePage(latestEditableContentRef.current);
        const titlePart = latestEditableContentRef.current.substring(0, latestEditableContentRef.current.length - bodyContent.length);
        const sourceLines = normalizeNewlines(bodyContent).split('\n');
        if (lineKey >= 0 && lineKey < sourceLines.length) {
          // Preserve dual-dialogue marker (^) because the DOM display strips it.
          const existingSource = sourceLines[lineKey] || '';
          if (currentLineType === 'character' && existingSource.trimEnd().endsWith('^')) {
            sourceLines[lineKey] = currentText.replace(/\s*\^$/, '').trimEnd() + ' ^';
          } else {
            sourceLines[lineKey] = currentText;
          }
        }

        let insertLines;
        let focusOffset = 0;

        if (currentLineType === 'character') {
          // Character → parenthetical "()" with cursor between parens.
          insertLines = ['()'];
          focusOffset = 1;
        } else if (currentLineType === 'parenthetical') {
          const trimmedText = currentText.trim();
          if (trimmedText === '()' || trimmedText === '(' || trimmedText === '') {
            // Empty parenthetical → dialogue (SYNCHRONOUS DOM mutation).
            // Directly convert this element to dialogue without React re-render.
            lineEl.textContent = '';
            lineEl.dataset.lineType = 'dialogue';
            lineEl.style.margin = '';
            lineEl.style.marginLeft = `${MEASUREMENTS.DIALOGUE_INDENT}in`;
            lineEl.style.marginRight = `${DIALOGUE_RIGHT_MARGIN}in`;
            lineEl.style.marginTop = '0';
            lineEl.style.marginBottom = '0';
            lineEl.style.textTransform = 'none';
            sourceLines[lineKey] = '!';
            const updated = titlePart + sourceLines.join('\n');
            latestEditableContentRef.current = updated;
            hasUnsavedChangesRef.current = true;
            if (!isApplyingHistoryRef.current) pushHistory(updated);
            lineEl.focus();
            setCursorAtOffset(lineEl, 0);
            return;
          } else {
            // Parenthetical with content → dialogue placeholder.
            // \u200B (zero-width space) survives trim(), Fountain parses as dialogue
            // after parenthetical, spacing logic treats as dialogue continuation.
            insertLines = ['\u200B'];
          }
        } else if (currentLineType === 'dialogue') {
          // Dialogue → blank + action (exits dialogue block)
          insertLines = ['', '!'];
        } else {
          // Scene, action, transition, etc. → blank + action
          insertLines = ['', '!'];
        }

        // Insert new line(s) into source, trigger React re-render, poll for focus
        const insertAt = (typeof lineKey === 'number' && !isNaN(lineKey)) ? lineKey + 1 : sourceLines.length;
        sourceLines.splice(insertAt, 0, ...insertLines);
        const updated = titlePart + sourceLines.join('\n');
        latestEditableContentRef.current = updated;
        focusedLineKeyRef.current = null;
        setFocusedLineKey(null);
        pendingRepaginateRef.current = false;
        setEditableContent(updated);
        setHasUnsavedChanges(true);
        if (!isApplyingHistoryRef.current) pushHistory(updated);

        const targetKey = insertAt + insertLines.length - 1;
        const isDialoguePlaceholder = currentLineType === 'parenthetical';
        const poll = (n) => {
          if (n <= 0) return;
          const el = document.querySelector(`[data-source-key="${targetKey}"]`);
          if (el && el.isContentEditable) {
            // Clear placeholder text so user sees empty line
            if (isDialoguePlaceholder) {
              el.textContent = '';
            } else if (focusOffset === 0 && el.innerText === '!') {
              el.textContent = '';
            }
            el.focus();
            setCursorAtOffset(el, focusOffset);
          } else {
            setTimeout(() => poll(n - 1), 50);
          }
        };
        setTimeout(() => poll(20), 50);
        return;
      }

      // Backspace at start — merge with previous line
      if (e.key === 'Backspace') {
        // If text is selected within this line, let the browser delete the
        // selection natively. Without this, a selection that starts at offset 0
        // would be read as cursorPos===0 and wrongly trigger a line merge.
        const domSel = window.getSelection();
        if (domSel && !domSel.isCollapsed) return;
        const cursorPos = getCursorOffsetInElement(lineEl);
        if (cursorPos === 0 && !isNaN(lineKey) && lineKey > 0) {
          e.preventDefault();
          const currentContent = latestEditableContentRef.current;
          if (!currentContent) return;
          const { bodyContent } = extractTitlePage(currentContent);
          const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
          const sourceLines = normalizeNewlines(bodyContent).split('\n');
          if (lineKey < sourceLines.length) {
            const prevPlainLen = stripEmphasisMarkers(sourceLines[lineKey - 1]).length;
            sourceLines[lineKey - 1] += sourceLines[lineKey];
            sourceLines.splice(lineKey, 1);
            focusedLineKeyRef.current = null;
            pendingRepaginateRef.current = false;
            setEditableContent(titlePart + sourceLines.join('\n'));
            setHasUnsavedChanges(true);
            const targetKey = lineKey - 1;
            setTimeout(() => {
              const prevEl = document.querySelector(`[data-source-key="${targetKey}"]`);
              if (prevEl) { prevEl.focus(); setCursorAtOffset(prevEl, prevPlainLen); }
            }, 300);
          }
        }
        return;
      }

      // Arrow key navigation
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const cursorPos = getCursorOffsetInElement(lineEl);
        const textLen = lineEl.innerText?.length || 0;
        const atStart = cursorPos === 0;
        const atEnd = cursorPos >= textLen;
        if ((e.key === 'ArrowUp' && atStart) || (e.key === 'ArrowDown' && atEnd)) {
          const editables = editableLinesRef.current.length
            ? editableLinesRef.current
            : Array.from(document.querySelectorAll('[data-source-key]')).filter(el => el.isContentEditable);
          const currentKey = String(lineKey);
          let currentIdx = -1;
          for (let i = 0; i < editables.length; i++) {
            if (editables[i].dataset.sourceKey === currentKey) { currentIdx = i; break; }
          }
          const nextIdx = e.key === 'ArrowUp' ? currentIdx - 1 : currentIdx + 1;
          if (nextIdx >= 0 && nextIdx < editables.length) {
            e.preventDefault();
            const target = editables[nextIdx];
            target.focus();
            setCursorAtOffset(target, e.key === 'ArrowUp' ? (target.innerText?.length || 0) : 0);
          }
        }
        return;
      }

      // Emphasis shortcuts: Ctrl+B/I/U
      if (e.ctrlKey && !e.shiftKey) {
        const key = e.key.toUpperCase();
        if (key === 'B') { e.preventDefault(); wrapSelectionWithRef.current?.('**', '**'); return; }
        if (key === 'I') { e.preventDefault(); wrapSelectionWithRef.current?.('*', '*'); return; }
        if (key === 'U') { e.preventDefault(); wrapSelectionWithRef.current?.('_', '_'); return; }
      }

      // Ctrl+Shift element type conversion shortcuts
      if (e.ctrlKey && e.shiftKey) {
        const key = e.key.toUpperCase();
        const typeMap = {
          'A': 'action', 'C': 'character',
          'D': 'dualDialogue', 'E': 'centered', 'G': 'dialogue',
          'H': 'scene', 'L': 'lyric',
          'P': 'parenthetical', 'T': 'transition'
        };
        if (typeMap[key]) { e.preventDefault(); convertLineType(typeMap[key]); return; }
        if (key === 'ENTER') { e.preventDefault(); insertSnippetAtCursorRef.current?.(QUICK_INSERT_SNIPPETS.pageBreak); return; }
      }
    };

    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isLocked, convertLineType, applyAutocompleteSuggestion]);

  // ─── Multi-line selection ────────────────────────────────────────────────────

  // getSourceKeyFromEl is hoisted outside the component (pure function)

  // Helper: keep multiLineSelectionRef in sync and update state
  const setMultiSel = useCallback((sel) => {
    multiLineSelectionRef.current = sel;
    setMultiLineSelection(sel);
  }, []);

  // Helper: clear multi-line selection
  const clearMultiSel = useCallback(() => {
    multiLineSelectionRef.current = null;
    setMultiLineSelection(null);
    dragAnchorKeyRef.current = null;
    isDraggingRef.current = false;
  }, []);

  // Helper: delete all source lines covered by current multi-line selection,
  // replace them with a single empty line at the start position.
  const deleteMultiLineSelection = useCallback(() => {
    const sel = multiLineSelectionRef.current;
    if (!sel) return;
    const currentContent = latestEditableContentRef.current;
    if (!currentContent) return;
    const { bodyContent } = extractTitlePage(currentContent);
    const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
    const sourceLines = normalizeNewlines(bodyContent).split('\n');
    const lo = Math.min(sel.startKey, sel.endKey);
    const hi = Math.max(sel.startKey, sel.endKey);
    // Replace the selected range with a single empty action line
    sourceLines.splice(lo, hi - lo + 1, '');
    clearMultiSel();
    focusedLineKeyRef.current = null;
    pendingRepaginateRef.current = false;
    const updated = titlePart + sourceLines.join('\n');
    latestEditableContentRef.current = updated;
    setEditableContent(updated);
    setHasUnsavedChanges(true);
    if (!isApplyingHistoryRef.current) pushHistory(updated);
    // Refocus at lo
    setTimeout(() => {
      const el = document.querySelector(`[data-source-key="${lo}"]`);
      if (el && el.isContentEditable) { el.focus(); setCursorAtOffset(el, 0); }
    }, 300);
  }, [clearMultiSel, pushHistory]);

  // Mouse down — record anchor line
  useEffect(() => {
    if (isLocked) return;
    const container = pagesContainerRef.current;
    if (!container) return;

    const onMouseDown = (e) => {
      const key = getSourceKeyFromEl(e.target);
      if (key === null) { clearMultiSel(); return; }
      dragAnchorKeyRef.current = key;
      isDraggingRef.current = false;
      // Clear any existing multi-selection on fresh click
      if (multiLineSelectionRef.current) clearMultiSel();
    };

    const onMouseMove = (e) => {
      if (dragAnchorKeyRef.current === null) return;
      if (!(e.buttons & 1)) { dragAnchorKeyRef.current = null; isDraggingRef.current = false; return; }
      const key = getSourceKeyFromEl(e.target);
      if (key === null) return;
      if (key !== dragAnchorKeyRef.current) {
        isDraggingRef.current = true;
        setMultiSel({ startKey: dragAnchorKeyRef.current, endKey: key });
      }
    };

    const onMouseUp = (e) => {
      if (!isDraggingRef.current) {
        dragAnchorKeyRef.current = null;
        return;
      }
      const key = getSourceKeyFromEl(e.target);
      if (key !== null && key !== dragAnchorKeyRef.current) {
        setMultiSel({ startKey: dragAnchorKeyRef.current, endKey: key });
      } else if (key === dragAnchorKeyRef.current) {
        clearMultiSel();
      }
      dragAnchorKeyRef.current = null;
      isDraggingRef.current = false;
    };

    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseup', onMouseUp);
    };
  }, [isLocked, clearMultiSel, setMultiSel]);

  // Keyboard handler for multi-line selection — runs before the main keydown handler
  useEffect(() => {
    if (isLocked) return;
    const handler = (e) => {
      const sel = multiLineSelectionRef.current;
      if (!sel) return;

      // Escape — clear selection
      if (e.key === 'Escape') { e.preventDefault(); clearMultiSel(); return; }

      // Ctrl+C — copy selected lines text to clipboard
      if (e.ctrlKey && e.key.toUpperCase() === 'C') {
        const currentContent = latestEditableContentRef.current;
        if (!currentContent) return;
        const { bodyContent } = extractTitlePage(currentContent);
        const sourceLines = normalizeNewlines(bodyContent).split('\n');
        const lo = Math.min(sel.startKey, sel.endKey);
        const hi = Math.max(sel.startKey, sel.endKey);
        const copied = sourceLines.slice(lo, hi + 1).join('\n');
        navigator.clipboard?.writeText(copied).catch(() => {});
        return;
      }

      // Delete or Backspace — remove selected lines
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteMultiLineSelection();
        return;
      }

      // Any printable character — replace selection with that character
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const currentContent = latestEditableContentRef.current;
        if (!currentContent) return;
        const { bodyContent } = extractTitlePage(currentContent);
        const titlePart = currentContent.substring(0, currentContent.length - bodyContent.length);
        const sourceLines = normalizeNewlines(bodyContent).split('\n');
        const lo = Math.min(sel.startKey, sel.endKey);
        const hi = Math.max(sel.startKey, sel.endKey);
        // Replace selection range with single line containing just the typed char
        sourceLines.splice(lo, hi - lo + 1, e.key);
        clearMultiSel();
        focusedLineKeyRef.current = null;
        pendingRepaginateRef.current = false;
        const updated = titlePart + sourceLines.join('\n');
        latestEditableContentRef.current = updated;
        setEditableContent(updated);
        setHasUnsavedChanges(true);
        if (!isApplyingHistoryRef.current) pushHistory(updated);
        setTimeout(() => {
          const el = document.querySelector(`[data-source-key="${lo}"]`);
          if (el && el.isContentEditable) { el.focus(); setCursorAtOffset(el, 1); }
        }, 300);
        return;
      }
    };
    // Use capture so we intercept before the main keydown handler
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isLocked, clearMultiSel, deleteMultiLineSelection]);

  // Ctrl+A — select all lines on the focused page
  useEffect(() => {
    if (isLocked) return;
    const handler = (e) => {
      if (e.ctrlKey && e.key.toUpperCase() === 'A' && !e.shiftKey) {
        const container = pagesContainerRef.current;
        if (!container) return;
        const allLines = Array.from(container.querySelectorAll('[data-source-key]'))
          .filter(el => el.isContentEditable)
          .map(el => parseInt(el.dataset.sourceKey))
          .filter(k => !isNaN(k));
        if (allLines.length < 2) return;
        e.preventDefault();
        setMultiSel({ startKey: allLines[0], endKey: allLines[allLines.length - 1] });
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isLocked, setMultiSel]);

  // Auto-format screenplay — only uppercase scene headings and known transitions.
  // Avoids the previous bug of uppercasing ALL all-caps lines (which includes
  // legitimate all-caps action like "THE DOOR SLAMS").
  const autoFormatScreenplay = (text) => {
    // Format scene headings (must start with a known prefix)
    text = text.replace(/^((?:INT\.|EXT\.|INT\/EXT\.|INT\.\/EXT\.|I\/E\.|EST\.|ESTABLISHING|INTERCUT|FLASHBACK|MONTAGE|SERIES OF SHOTS|BEGIN MONTAGE|END MONTAGE|POV|INSERT)\b.*)$/gim, match => match.toUpperCase());
    // Format known transitions only
    text = text.replace(/^(CUT TO:|FADE IN:|FADE OUT:?|DISSOLVE TO:|SMASH CUT TO:|MATCH CUT TO:|JUMP CUT TO:|WIPE TO:)$/gim, match => match.toUpperCase().trim());
    return text;
  };

  // Update a title page field in the Fountain source
  const handleTitlePageEdit = useCallback((field, newValue) => {
    const current = latestEditableContentRef.current;
    if (!current) return;
    const normalized = normalizeNewlines(current);
    // Match the field line in the title page block (case-insensitive)
    const fieldRegex = new RegExp(`^(${field}:\\s*)(.*)$`, 'im');
    const match = normalized.match(fieldRegex);
    if (match) {
      const updated = normalized.replace(fieldRegex, `${match[1]}${newValue}`);
      latestEditableContentRef.current = updated;
      setEditableContent(updated);
      setHasUnsavedChanges(true);
      if (!isApplyingHistoryRef.current) pushHistory(updated);
    }
  }, [pushHistory]);

  // Editable title page field helper
  const editableTitleField = (value, field, sx) => {
    if (!value && !field) return null;
    return (
      <Typography
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => {
          const newVal = e.currentTarget.innerText.trim();
          if (newVal !== value) {
            handleTitlePageEdit(field, newVal);
          }
        }}
        sx={{ ...sx, outline: 'none', cursor: 'text', minWidth: '2em' }}
      >
        {value || ''}
      </Typography>
    );
  };

  // Render a Hollywood-style title page (no page number) — fields are editable
  const renderTitlePage = () => {
    if (!titlePage) return null;
    const { title, credit, author, source, draft, copyright, contact } = titlePage;

    return (
      <Box
        className="script-page"
        sx={{
          width: `${MEASUREMENTS.PAGE_WIDTH}in`,
          height: `${MEASUREMENTS.PAGE_HEIGHT}in`,
          padding: `${MEASUREMENTS.TOP_MARGIN}in ${MEASUREMENTS.RIGHT_MARGIN}in ${MEASUREMENTS.BOTTOM_MARGIN}in ${MEASUREMENTS.LEFT_MARGIN}in`,
          backgroundColor: isDarkMode ? '#0b0b0b' : 'white',
          color: isDarkMode ? darkModeColors.text : 'inherit',
          boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.5)' : '0 0 10px rgba(0,0,0,0.1)',
          position: 'relative',
          pageBreakAfter: 'always',
          pageBreakInside: 'avoid',
          pageBreakBefore: 'always',
          breakAfter: 'page',
          breakBefore: 'page',
          breakInside: 'avoid',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          '@media print': {
            boxShadow: 'none',
            margin: 0,
            pageBreakAfter: 'always',
            pageBreakBefore: 'always',
            pageBreakInside: 'avoid',
            breakAfter: 'page',
            breakBefore: 'page',
            breakInside: 'avoid',
            height: `${MEASUREMENTS.PAGE_HEIGHT}in`,
            width: `${MEASUREMENTS.PAGE_WIDTH}in`,
            boxSizing: 'border-box',
            overflow: 'hidden',
            padding: `${MEASUREMENTS.TOP_MARGIN}in ${MEASUREMENTS.RIGHT_MARGIN}in ${MEASUREMENTS.BOTTOM_MARGIN}in ${MEASUREMENTS.LEFT_MARGIN}in`
          }
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '8pt',
            paddingTop: '2.5in', // Title at ~3.5" from page top (1" margin + 2.5") = ~1/3 down
            fontFamily: baseStyles.fontFamily,
            fontSize: baseStyles.fontSize,
            lineHeight: '1'
          }}
        >
          {title != null ? editableTitleField(title, 'Title', {
            fontFamily: baseStyles.fontFamily, fontSize: '12pt', fontWeight: 700, textTransform: 'uppercase', textAlign: 'center'
          }) : null}
          {credit ? (
            <Typography sx={{ fontFamily: baseStyles.fontFamily, fontSize: '12pt', textAlign: 'center', whiteSpace: 'pre-line' }}>
              {renderEmphasis(credit)}
            </Typography>
          ) : null}
          {/* Two blank lines between credit and author (standard) */}
          {(author && credit) && <Box sx={{ height: '24pt' }} />}
          {author != null ? (
            <>
              {/* Only show "by" if no credit line already says "Written by" */}
              {!credit && (
                <Typography sx={{ fontFamily: baseStyles.fontFamily, fontSize: '12pt', textAlign: 'center' }}>
                  by
                </Typography>
              )}
              {editableTitleField(author, 'Author', {
                fontFamily: baseStyles.fontFamily, fontSize: '12pt', textAlign: 'center'
              })}
            </>
          ) : null}
          {source ? (
            <Typography sx={{ fontFamily: baseStyles.fontFamily, fontSize: '12pt', textAlign: 'center', whiteSpace: 'pre-line' }}>
              {renderEmphasis(source)}
            </Typography>
          ) : null}
        </Box>
        { (draft || copyright || contact) && (
          <Box
            sx={{
              position: 'absolute',
              left: `${MEASUREMENTS.LEFT_MARGIN}in`,
              bottom: `${MEASUREMENTS.BOTTOM_MARGIN}in`,
              width: `calc(100% - ${MEASUREMENTS.LEFT_MARGIN + MEASUREMENTS.RIGHT_MARGIN}in)`,
              fontFamily: 'Courier Prime, Courier, monospace',
              fontSize: '12pt',
              whiteSpace: 'pre-line',
              lineHeight: '1.2',
              textAlign: 'left'
            }}
          >
            {draft && editableTitleField(draft, 'Draft date', {
              fontSize: '12pt', fontWeight: 400, fontFamily: 'Courier Prime, Courier, monospace'
            })}
            {copyright && editableTitleField(copyright, 'Copyright', {
              fontSize: '10pt', fontWeight: 400, mt: 0.5, fontFamily: 'Courier Prime, Courier, monospace'
            })}
            {contact && editableTitleField(contact, 'Contact', {
              fontSize: '12pt', fontWeight: 400, mt: 0.5, fontFamily: 'Courier Prime, Courier, monospace'
            })}
          </Box>
        )}
      </Box>
    );
  };

  // Render tagged content (tag highlights overlaid on text)
  const renderTaggedContent = (line, index) => {
    if (!showTags) return renderEmphasis(line.text);
    const lineTags = tags.filter(tag => tag.lineNumber === index);
    if (lineTags.length === 0) return renderEmphasis(line.text);

    const segments = [];
    const text = line.text;

    // Find all tag positions, then resolve overlaps by keeping the earliest
    // starting tag and dropping any tag that overlaps with an already-placed one.
    const tagPositions = lineTags
      .map(tag => ({ tag, start: text.indexOf(tag.text) }))
      .filter(tp => tp.start !== -1)
      .sort((a, b) => a.start - b.start || b.tag.text.length - a.tag.text.length);

    const placed = [];
    for (const tp of tagPositions) {
      const end = tp.start + tp.tag.text.length;
      // Check for overlap with any already-placed tag
      const overlaps = placed.some(p => tp.start < p.end && end > p.start);
      if (!overlaps) {
        placed.push({ ...tp, end });
      }
    }

    let currentPosition = 0;
    for (const tp of placed) {
      if (tp.start > currentPosition) {
        segments.push({ text: text.substring(currentPosition, tp.start), tags: [] });
      }
      segments.push({ text: tp.tag.text, tags: [tp.tag] });
      currentPosition = tp.end;
    }

    if (currentPosition < text.length) {
      segments.push({ text: text.substring(currentPosition), tags: [] });
    }

    return segments.map((segment, segIndex) => {
      if (segment.tags.length === 0) {
        return <span key={segIndex}>{renderEmphasis(segment.text)}</span>;
      }

      const tagType = segment.tags[0].type === 'CAST' ? 'CHARACTER' : segment.tags[0].type;
      const tagInfo = TAG_TYPES[tagType] || { label: tagType, color: segment.tags[0].color || '#9575CD' };
      const bgOpacity = isDarkMode ? '66' : '33';
      const tagColor = segment.tags[0].color || tagInfo.color;

      return (
        <span
          key={segIndex}
          style={{
            backgroundColor: `${tagColor}${bgOpacity}`,
            cursor: 'pointer',
            padding: '0 2px',
            margin: '0 -1px',
            borderRadius: '2px',
            transition: 'background-color 0.2s',
            border: isDarkMode ? `1px solid ${tagColor}88` : 'none',
            color: isDarkMode ? '#ffffff' : 'inherit'
          }}
          title={`${tagInfo.label} (click to remove)`}
          onClick={(e) => {
            e.stopPropagation();
            setTagRemovalConfirm({ tag: segment.tags[0], label: tagInfo.label });
          }}
        >
          {renderEmphasis(segment.text)}
        </span>
      );
    });
  };

  // Render a single column of lines for dual dialogue
  const renderDualColumn = (colLines) => {
    // Each column gets ~45% of content width with a small center gutter
    const colWidth = `calc(50% - 0.15in)`;
    return (
      <div style={{ width: colWidth, flexShrink: 0 }}>
        {colLines.map((colLine, ci) => {
          const colSourceKey = typeof colLine.key === 'number' ? colLine.key : `dual-col-${ci}`;
          const colIsFocused = focusedLineKey === colLine.key;
          let colStyle = isDarkMode
            ? { ...(darkModeElementStyles[colLine.type] || darkModeElementStyles.action) }
            : { ...(elementStyles[colLine.type] || elementStyles.action) };

          // Scale indents proportionally for half-width columns.
          // Standard dual dialogue keeps character/parenthetical/dialogue
          // proportions relative to the column width.
          if (colLine.type === 'character') {
            colStyle = { ...colStyle, marginLeft: `${MEASUREMENTS.CHARACTER_INDENT * 0.45}in` };
          } else if (colLine.type === 'parenthetical') {
            colStyle = { ...colStyle, marginLeft: `${MEASUREMENTS.PARENTHETICAL_INDENT * 0.45}in`, marginRight: `${MEASUREMENTS.PARENTHETICAL_INDENT * 0.3}in` };
          } else if (colLine.type === 'dialogue' || colLine.type === 'lyric') {
            colStyle = { ...colStyle, marginLeft: `${MEASUREMENTS.DIALOGUE_INDENT * 0.45}in`, marginRight: `${MEASUREMENTS.DIALOGUE_INDENT * 0.3}in` };
          }

          const editColStyle = { ...colStyle, outline: 'none', cursor: isLocked ? 'default' : 'text', minHeight: '1em', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' };

          // Strip zero-width space placeholder from displayed text.
          // Parenthetical lines: show '()' when user inserted it, hide when it's the ZWS placeholder.
          let displayText = (colLine.text || '');
          const hadZWS = displayText.includes('\u200B');
          displayText = displayText.replace(/\u200B/g, '');
          // Only hide () if it came from the ZWS placeholder (hadZWS), not from user Enter insertion
          if (colLine.type === 'parenthetical' && hadZWS && displayText.trim() === '()') {
            displayText = '';
          }

          return (
            <EditableLine
              key={colSourceKey}
              index={ci}
              sourceKey={colSourceKey}
              lineType={colLine.type}
              isLocked={isLocked}
              isFocused={colIsFocused}
              style={editColStyle}
              onInput={isLocked ? undefined : (e) => handleLineInput(colSourceKey, e)}
              onFocus={isLocked ? undefined : (e) => {
                dualFocusMoveRef.current = false;
                focusedLineKeyRef.current = colSourceKey;
                lastFocusedLineKeyRef.current = colSourceKey;
                setFocusedLineKey(colSourceKey);
                updateActiveSceneForKey(colSourceKey);
                // Clear placeholder content from DOM on focus so user types into
                // a clean field. Only clear: empty string, ZWS-only — NOT () which user inserted.
                const el = e.currentTarget;
                const domText = (el.textContent || '').replace(/\u200B/g, '').trim();
                if (domText === '') {
                  el.textContent = '';
                }
              }}
              onBlur={isLocked ? undefined : (e) => {
                focusedLineKeyRef.current = null;
                setFocusedLineKey(null);
                // Defer repagination: if focus moves to another line in the same
                // dual block, skip the setEditableContent call entirely to avoid
                // resetting the DOM of sibling lines mid-edit.
                const relatedTarget = e.relatedTarget;
                const stayingInBlock = dualFocusMoveRef.current || (relatedTarget &&
                  relatedTarget.closest?.('[data-line-type="dualDialogue"]') === e.currentTarget.closest?.('[data-line-type="dualDialogue"]'));
                if (stayingInBlock) {
                  // Just update the ref — don't repaginate
                  if (hasUnsavedChangesRef.current) {
                    hasUnsavedChangesRef.current = false;
                    setHasUnsavedChanges(true);
                  }
                  return;
                }
                setEditableContent(latestEditableContentRef.current);
                if (hasUnsavedChangesRef.current) {
                  setHasUnsavedChanges(true);
                  hasUnsavedChangesRef.current = false;
                }
                if (pendingRepaginateRef.current) doPaginate();
              }}
              onMouseUp={(disabledTagging || !showTags) ? undefined : handleTextSelection}
            >
              {displayText}
            </EditableLine>
          );
        })}
      </div>
    );
  };

  // Render a single screenplay line — inline-editable
  const renderLine = (line, index) => {
    // Sections and synopses are structural markers — not rendered on the page,
    // but kept in the line array so the Scene Navigator can list them.
    if (line.type === 'section' || line.type === 'synopsis') {
      return null;
    }
    const isFocused = focusedLineKey === line.key;
    const isBlankLine = line.type === 'blank';
    const sourceKey = typeof line.key === 'number' ? line.key : index;
    const baseStyle = isDarkMode ? darkModeElementStyles[line.type] : elementStyles[line.type];

    // Determine if this line is within the current multi-line selection
    const isMultiSelected = (() => {
      const sel = multiLineSelection;
      if (!sel || typeof sourceKey !== 'number') return false;
      const lo = Math.min(sel.startKey, sel.endKey);
      const hi = Math.max(sel.startKey, sel.endKey);
      return sourceKey >= lo && sourceKey <= hi;
    })();

    // Multi-line selection highlight: a single flat shade that matches the
    // browser's native text-selection blue (no darker anchor-line distinction).
    const multiSelectOverlay = isMultiSelected ? {
      backgroundColor: isDarkMode ? 'rgba(51,144,255,0.35)' : 'rgba(51,144,255,0.30)',
      borderRadius: '2px',
      outline: 'none',
    } : {};

    // Dual dialogue: two editable columns side by side
    if (line.type === 'dualDialogue') {
      return (
        <div
          key={index}
          data-line-number={index}
          data-line-type="dualDialogue"
          style={{
            display: 'flex',
            gap: '0.3in',
            width: '100%',
            alignItems: 'flex-start'
          }}
        >
          {renderDualColumn(line.left || [])}
          {renderDualColumn(line.right || [])}
        </div>
      );
    }

    // Blank lines: not editable, just spacing
    if (isBlankLine) {
      return (
        <div
          key={index}
          data-line-number={index}
          data-source-key={sourceKey}
          data-line-type={line.type}
          style={{ ...baseStyle, ...multiSelectOverlay }}
        >
          &nbsp;
        </div>
      );
    }

    // (MORE) and (CONT'D) are auto-generated pagination markers — not editable
    if (line.isMore || line.isContinued) {
      return (
        <div
          key={index}
          data-line-number={index}
          data-line-type={line.type}
          style={{ ...baseStyle, userSelect: 'none' }}
        >
          {line.text}
        </div>
      );
    }

    // Scene headings with scene numbers: render number on left & right (Hollywood style)
    if (line.type === 'scene' && line.sceneNumber) {
      const sceneStyle = isDarkMode ? darkModeElementStyles.scene : elementStyles.scene;
      return (
        <div
          key={index}
          data-line-number={index}
          data-source-key={sourceKey}
          data-line-type={line.type}
          style={{
            ...sceneStyle,
            ...multiSelectOverlay,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            cursor: isLocked ? 'default' : 'text',
          }}
        >
          <span style={{ flexShrink: 0, marginRight: '0.5rem' }}>{line.sceneNumber}</span>
          <EditableLine
            key={index}
            index={index}
            sourceKey={sourceKey}
            lineType={line.type}
            isLocked={isLocked}
            isFocused={isFocused}
            style={{ ...sceneStyle, flex: 1, outline: 'none', minHeight: '1em', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}
            onInput={isLocked ? undefined : (e) => handleLineInput(sourceKey, e)}
            onFocus={isLocked ? undefined : () => {
              focusedLineKeyRef.current = sourceKey;
              lastFocusedLineKeyRef.current = sourceKey;
              setFocusedLineKey(sourceKey);
              updateActiveSceneForKey(sourceKey);
            }}
            onBlur={isLocked ? undefined : () => {
              focusedLineKeyRef.current = null;
              setFocusedLineKey(null);
              setEditableContent(latestEditableContentRef.current);
              if (hasUnsavedChangesRef.current) {
                setHasUnsavedChanges(true);
                hasUnsavedChangesRef.current = false;
              }
              if (pendingRepaginateRef.current) {
                doPaginate();
              }
            }}
          >
            {line.text}
          </EditableLine>
          <span style={{ flexShrink: 0, marginLeft: '0.5rem' }}>{line.sceneNumber}</span>
        </div>
      );
    }

    // Editable line wrapped in React.memo — skips re-render while focused
    // so React never touches the contentEditable DOM and resets the cursor.
    const editStyle = {
      ...baseStyle,
      ...multiSelectOverlay,
      outline: isMultiSelected ? multiSelectOverlay.outline : 'none',
      cursor: isLocked ? 'default' : 'text',
      minHeight: '1em',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word',
      wordBreak: 'break-word'
    };

    const editableLine = (
      <EditableLine
        key={index}
        index={index}
        sourceKey={sourceKey}
        lineType={line.type}
        isLocked={isLocked}
        isFocused={isFocused}
        style={editStyle}
        onInput={isLocked ? undefined : (e) => handleLineInput(sourceKey, e)}
        onFocus={isLocked ? undefined : () => {
          focusedLineKeyRef.current = sourceKey;
          lastFocusedLineKeyRef.current = sourceKey;
          setFocusedLineKey(sourceKey);
          updateActiveSceneForKey(sourceKey);
        }}
        onBlur={isLocked ? undefined : () => {
          focusedLineKeyRef.current = null;
          setFocusedLineKey(null);
          // Flush edit so React state is in sync before repagination
          setEditableContent(latestEditableContentRef.current);
          if (hasUnsavedChangesRef.current) {
            setHasUnsavedChanges(true);
            hasUnsavedChangesRef.current = false;
          }
          // Flush deferred re-pagination now that editing is done
          if (pendingRepaginateRef.current) {
            doPaginate();
          }
        }}
        onMouseUp={(disabledTagging || !showTags) ? undefined : handleTextSelection}
      >
        {renderTaggedContent(line, index)}
      </EditableLine>
    );

    // For character lines with isDual flag, show a small "DUAL →" badge so writers
    // can see at a glance that this character triggers a dual dialogue column.
    if (line.type === 'character' && line.isDual) {
      return (
        <div key={index} style={{ position: 'relative', display: 'block' }}>
          {editableLine}
          <span
            title="Dual dialogue right column (Ctrl+Shift+D to toggle)"
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '8pt',
              fontFamily: 'sans-serif',
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: isDarkMode ? '#90CAF9' : '#1565C0',
              background: isDarkMode ? '#1A237E33' : '#E3F2FD',
              border: `1px solid ${isDarkMode ? '#42A5F5' : '#90CAF9'}`,
              borderRadius: '3px',
              padding: '0 4px',
              lineHeight: '1.4',
              pointerEvents: 'none',
              userSelect: 'none',
              whiteSpace: 'nowrap'
            }}
          >
            DUAL ⇒
          </span>
        </div>
      );
    }

    return editableLine;
  };

  const updateActiveSceneForKey = useCallback((lineKey) => {
    if (lineKey == null || !sceneHeadings.length) return;
    let latestSceneKey = sceneHeadings[0].key;
    for (let i = 0; i < sceneHeadings.length; i++) {
      if (sceneHeadings[i].key <= lineKey) {
        latestSceneKey = sceneHeadings[i].key;
      } else {
        break;
      }
    }
    setActiveSceneKey(latestSceneKey);
  }, [sceneHeadings]);

  const scrollToScene = useCallback((lineKey) => {
    if (lineKey == null) return;
    const container = pagesContainerRef.current;
    if (!container) return;
    let el = container.querySelector(`[data-source-key="${lineKey}"]`);
    // Sections/synopses don't render DOM elements — find the nearest scene heading
    // at or after this key to scroll to instead.
    if (!el && sceneHeadings.length) {
      const idx = sceneHeadings.findIndex(s => s.key === lineKey);
      if (idx !== -1) {
        for (let i = idx; i < sceneHeadings.length; i++) {
          if (sceneHeadings[i].type === 'scene') {
            el = container.querySelector(`[data-source-key="${sceneHeadings[i].key}"]`);
            if (el) break;
          }
        }
        // Fallback: scroll to the previous scene heading
        if (!el) {
          for (let i = idx - 1; i >= 0; i--) {
            if (sceneHeadings[i].type === 'scene') {
              el = container.querySelector(`[data-source-key="${sceneHeadings[i].key}"]`);
              if (el) break;
            }
          }
        }
      }
    }
    if (el) {
      // Manually scroll only the pages container — scrollIntoView would also
      // scroll ancestor containers (TabPanel, Paper, etc.) and push the
      // toolbar out of view.
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      const offset = eRect.top - cRect.top;
      const target = container.scrollTop + offset - cRect.height / 2 + eRect.height / 2;
      container.scrollTo({ top: target, behavior: 'smooth' });
      if (!isLocked && el.isContentEditable) {
        el.focus({ preventScroll: true });
      }
      setActiveSceneKey(lineKey);
    }
  }, [isLocked, sceneHeadings]);

  useEffect(() => {
    if (!sceneHeadings.length) {
      setActiveSceneKey(null);
      return;
    }
    if (!sceneHeadings.some(scene => scene.key === activeSceneKey)) {
      setActiveSceneKey(sceneHeadings[0].key);
    }
  }, [sceneHeadings, activeSceneKey]);

  if (!content) {
    return <Typography>No content to display</Typography>;
  }

  // Tag menu items with safety check
  const tagMenuItems = Object.entries(TAG_TYPES).map(([type, tagInfo]) => ({
    type,
    label: tagInfo?.label || type,
    color: tagInfo?.color || '#9575CD'
  }));

  return (
    <>
      {/* Font embed + print isolation + standard Letter sizing and margins */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
        :root { --screenplay-font: 'Courier Prime', Courier, monospace; }

        /* Uniform text-selection highlight across ALL screenplay element types
           (slugline, action, character, dialogue, etc.) so the in-line selection
           color is consistent and matches the multi-line selection overlay.
           Targets the editable lines and any nested emphasis/tag spans. */
        [data-source-key]::selection,
        [data-source-key] *::selection {
          background: ${isDarkMode ? 'rgba(51,144,255,0.45)' : 'rgba(51,144,255,0.40)'};
          color: ${isDarkMode ? '#ffffff' : '#000000'};
        }
        [data-source-key]::-moz-selection,
        [data-source-key] *::-moz-selection {
          background: ${isDarkMode ? 'rgba(51,144,255,0.45)' : 'rgba(51,144,255,0.40)'};
          color: ${isDarkMode ? '#ffffff' : '#000000'};
        }
        
        /* Print-specific class for the print container */
        .print-mode .hide-on-print { display: none !important; }
        .print-mode #screenplay-print-area { 
          display: block !important;
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 8.5in !important;
        }
        
        /* No @page margin - the script-page padding provides the actual screenplay margins */
        @page { size: 8.5in 11in; margin: 0; }
        @media print {
          /* Hide everything by default */
          body > * { display: none !important; }
          
          /* Show only our dedicated print container */
          #screenplay-dedicated-print {
            display: block !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 8.5in !important;
            font-family: var(--screenplay-font); 
            background: white !important; 
            color: #000 !important;
          }
          
          #screenplay-dedicated-print .script-page { 
            display: block !important;
            position: relative !important;
            page-break-after: always; 
            break-after: page; 
            background: white !important; 
            color: #000 !important;
            width: 8.5in !important;
            height: 11in !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          
          #screenplay-dedicated-print * { 
            background: transparent !important;
            color: #000 !important;
            box-shadow: none !important;
          }
        }
      `}</style>
      <Box sx={{ 
        backgroundColor: isDarkMode ? '#1e1e1e' : '#f0f2f5',
        color: isDarkMode ? darkModeColors.text : '#000000',
        height: '100%',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        '@media print': {
          display: 'block',
          height: 'auto',
          padding: 0,
          backgroundColor: 'white'
        }
      }}>
        {/* ─── Top Action Bar ─────────────────────────────────────────── */}
        <Box sx={{
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          '@media print': { display: 'none' }
        }}>
          {/* Title */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap', mr: 0.5 }}>
            Script Viewer
          </Typography>

          {/* Group: Edit Mode */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }}>
            <Tooltip title={isLocked ? 'Unlock to edit' : 'Lock (view only)'} arrow>
              <IconButton
                onClick={() => setIsLocked(prev => !prev)}
                size="small"
                sx={{
                  color: isLocked
                    ? (isDarkMode ? '#ff9800' : '#e65100')
                    : (isDarkMode ? '#81C784' : '#2e7d32'),
                  '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
                }}
              >
                {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: isLocked ? (isDarkMode ? '#ff9800' : '#e65100') : (isDarkMode ? '#81C784' : '#2e7d32'), userSelect: 'none' }}>
              {isLocked ? 'Locked' : 'Editing'}
            </Typography>
          </Box>

          {/* Group: Tags */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }}>
            <Tooltip title={showTags ? 'Hide tag highlights' : 'Show tag highlights'} arrow>
              <IconButton
                onClick={() => setShowTags(prev => !prev)}
                size="small"
                sx={{
                  color: showTags
                    ? (isDarkMode ? '#ffb74d' : '#ef6c00')
                    : (isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'),
                  '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
                }}
              >
                <SprayCanIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Auto-tag script (AI)" arrow>
              <IconButton
                onClick={handleAutoTag}
                disabled={isAutoTagging || disabledTagging}
                size="small"
                sx={{
                  color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                  '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
                  '&.Mui-disabled': { color: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' }
                }}
              >
                {isAutoTagging ? <CircularProgress size={16} /> : <AutoFixHighIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage tags" arrow>
              <IconButton
                onClick={() => setTagManagerOpen(true)}
                size="small"
                sx={{
                  color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                  '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Group: Export & Tools */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }}>
            <Tooltip title="Keyboard shortcuts" arrow>
              <IconButton
                size="small"
                onClick={(e) => setShortcutAnchor(e.currentTarget)}
                sx={{
                  color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                  '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
                }}
              >
                <KeyboardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onDownload && (
              <Tooltip title="Download" arrow>
                <IconButton
                  size="small"
                  onClick={onDownload}
                  sx={{
                    color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                    '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onPrint && (
              <Tooltip title="Print" arrow>
                <IconButton
                  size="small"
                  onClick={onPrint}
                  sx={{
                    color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                    '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
                  }}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onAnalyze && canAnalyze && (
              <Tooltip title="Analyze screenplay" arrow>
                <IconButton
                  size="small"
                  onClick={onAnalyze}
                  disabled={analyzeLoading}
                  sx={{
                    color: isDarkMode ? '#64B5F6' : '#1565C0',
                    '&:hover': { backgroundColor: isDarkMode ? 'rgba(100,181,246,0.12)' : 'rgba(21,101,192,0.08)' },
                    '&.Mui-disabled': { color: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' }
                  }}
                >
                  {analyzeLoading ? <CircularProgress size={16} /> : <AnalyticsIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Spacer + Save Status */}
          <Box sx={{ flex: 1 }} />
          {hasUnsavedChanges && !isSaving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', backgroundColor: isDarkMode ? 'rgba(255,193,7,0.1)' : 'rgba(255,193,7,0.08)' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'warning.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'warning.main', fontWeight: 500 }}>
                Unsaved
              </Typography>
            </Box>
          )}
          {isSaving && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }}>
              <CircularProgress size={12} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                Saving…
              </Typography>
            </Box>
          )}
          {saveError && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'error.main', fontWeight: 500 }}>
              {saveError}
            </Typography>
          )}
        </Box>
        {/* Locked banner */}
        {isLocked && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 1.5,
            mt: 0.5,
            px: 2,
            py: 1,
            borderRadius: '6px',
            flexShrink: 0,
            backgroundColor: isDarkMode ? 'rgba(255,152,0,0.12)' : 'rgba(230,81,0,0.08)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,152,0,0.3)' : 'rgba(230,81,0,0.2)',
            '@media print': {
              display: 'none'
            }
          }}>
            <LockIcon sx={{ fontSize: 18, color: isDarkMode ? '#ff9800' : '#e65100' }} />
            <Typography variant="body2" sx={{ color: isDarkMode ? '#ff9800' : '#e65100', fontWeight: 500 }}>
              View Only — Click the lock button above to enable editing
            </Typography>
          </Box>
        )}
        {/* Editor Toolbar — hidden when screenplay is locked */}
        {!isLocked && <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            mb: 1.5,
            mt: 0.5,
            px: 1,
            py: 0.75,
            borderRadius: '6px',
            flexShrink: 0,
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            '@media print': {
              display: 'none'
            }
          }}
        >
          {/* Title Page */}
          <Tooltip title="Insert Title Page" arrow>
            <Button
              size="small"
              onClick={addTitlePageTemplate}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: isDarkMode ? '#b0b0b0' : '#555',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                }
              }}
            >
              Title Page
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />

          {/* Emphasis Group */}
          {[
            { icon: FormatBoldIcon, tooltip: 'Bold (Ctrl+B)', prefix: '**', suffix: '**' },
            { icon: FormatItalicIcon, tooltip: 'Italic (Ctrl+I)', prefix: '*', suffix: '*' },
            { icon: FormatUnderlinedIcon, tooltip: 'Underline (Ctrl+U)', prefix: '_', suffix: '_' },
          ].map(({ icon: Icon, tooltip, prefix, suffix }) => (
            <Tooltip key={tooltip} title={tooltip} arrow>
              <IconButton
                size="small"
                onMouseDown={(e) => {
                  e.preventDefault();
                  // Capture selection BEFORE click clears it
                  const sel = window.getSelection();
                  if (sel && sel.rangeCount > 0) {
                    const range = sel.getRangeAt(0);
                    capturedSelectionRef.current = {
                      text: sel.toString(),
                      range: range.cloneRange(),
                      lineEl: document.activeElement?.dataset?.sourceKey !== undefined 
                        ? document.activeElement 
                        : sel.anchorNode?.parentElement?.closest('[data-source-key]')
                    };
                  }
                }}
                onClick={() => {
                  const captured = capturedSelectionRef.current;
                  if (captured && captured.text && captured.lineEl) {
                    wrapSelectionWith(prefix, suffix, captured);
                  } else {
                    wrapSelectionWith(prefix, suffix);
                  }
                  capturedSelectionRef.current = null;
                }}
                sx={{
                  borderRadius: '4px',
                  width: 28,
                  height: 28,
                  color: isDarkMode ? '#a0a0a0' : '#666',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                    color: isDarkMode ? '#fff' : '#000',
                  }
                }}
              >
                <Icon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ))}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />

          {/* Scene Helpers Group */}
          {[
            { label: 'INT/EXT', tooltip: 'Scene prefix', onClick: (e) => setScenePrefixAnchor(e.currentTarget), hasDropdown: true },
            { label: 'LOC', tooltip: 'Location', onClick: (e) => setLocationMenuAnchor(e.currentTarget), hasDropdown: true },
            { label: 'TIME', tooltip: 'Time of day', onClick: (e) => setTimeMenuAnchor(e.currentTarget), hasDropdown: true },
          ].map(({ label, tooltip, onClick, hasDropdown }) => (
            <Tooltip key={label} title={tooltip} arrow>
              <Button
                size="small"
                onClick={onClick}
                endIcon={hasDropdown ? <ArrowDropDownIcon sx={{ fontSize: '16px !important', ml: -0.5 }} /> : undefined}
                sx={{
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  fontFamily: 'Courier Prime, Courier, monospace',
                  letterSpacing: '0.02em',
                  color: isDarkMode ? '#a0a0a0' : '#666',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  }
                }}
              >
                {label}
              </Button>
            </Tooltip>
          ))}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />

          {/* Element Insert Group — converts current line type */}
          {QUICK_INSERTS.map(({ key, label, shortcut, icon: Icon }) => {
            const hasDropdown = key === 'transition' || key === 'character';
            const handleClick = (() => {
              if (key === 'transition') return (e) => setTransitionMenuAnchor(e.currentTarget);
              if (key === 'character') return (e) => setCharacterMenuAnchor(e.currentTarget);
              if (key === 'pageBreak') return () => insertSnippetAtCursor(QUICK_INSERT_SNIPPETS.pageBreak);
              return () => convertLineType(key);
            })();
            return (
              <Tooltip key={key} title={`${label} (${shortcut})`} arrow>
                <IconButton
                  size="small"
                  onClick={handleClick}
                  sx={{
                    borderRadius: '4px',
                    width: 30,
                    height: 30,
                    color: isDarkMode ? '#a0a0a0' : '#666',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                      color: isDarkMode ? '#fff' : '#000',
                    }
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                  {hasDropdown && <ArrowDropDownIcon sx={{ fontSize: 12, ml: -0.3 }} />}
                </IconButton>
              </Tooltip>
            );
          })}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Auto-save indicator */}
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)',
              fontSize: '0.65rem',
              userSelect: 'none',
            }}
          >
            Auto-saves
          </Typography>
        </Box>}

        <Box sx={{
          display: 'flex',
          gap: 2,
          flex: 1,
          minHeight: 0,
          flexDirection: 'row-reverse',
          '@media print': {
            display: 'block',
            flex: 'none',
            minHeight: 'auto',
            gap: 0
          }
        }}>
          {sceneHeadings.length > 0 && (
            <Paper
              sx={{
                width: 280,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: theme => theme.palette.mode === 'dark'
                  ? alpha('#121212', 0.6)
                  : '#ffffff',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0,0,0,0.2)'
                  : '0 1px 4px rgba(0,0,0,0.06)',
                '@media print': {
                  display: 'none'
                }
              }}
            >
              <Box sx={{ p: 2, borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
                <Box component="div" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.2 }}>
                  Scene Navigator
                </Box>
                <Box component="div" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {sceneHeadings.filter(s => s.type === 'scene').length} scenes
                  {sceneHeadings.some(s => s.type === 'section') && ` · ${sceneHeadings.filter(s => s.type === 'section').length} sections`}
                </Box>
                <TextField
                  size="small"
                  placeholder="Search sluglines"
                  value={sceneSearch}
                  onChange={(e) => setSceneSearch(e.target.value)}
                  fullWidth
                  sx={{ mt: 1.5 }}
                  InputProps={{
                    sx: { borderRadius: 1 }
                  }}
                />
              </Box>
              <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                {filteredScenes.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      disableTypography
                      primary={<span style={{ fontSize: '0.9rem', color: 'var(--mui-palette-text-secondary)' }}>No scenes match that search</span>}
                    />
                  </ListItem>
                ) : (
                  filteredScenes.map(scene => {
                    const isSection = scene.type === 'section';
                    const isSynopsis = scene.type === 'synopsis';
                    const indent = isSection ? `${(scene.level - 1) * 12}px` : '0';
                    return (
                    <ListItemButton
                      key={scene.key}
                      selected={scene.key === activeSceneKey}
                      onClick={() => scrollToScene(scene.key)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        mx: 1,
                        pl: isSection ? 2 : 1,
                        '&.Mui-selected': {
                          backgroundColor: theme => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
                          color: theme => theme.palette.mode === 'dark' ? '#fff' : theme.palette.primary.main
                        }
                      }}
                    >
                      <ListItemText
                        disableTypography
                        primary={
                          <span style={{
                            fontWeight: isSection ? 700 : (isSynopsis ? 400 : 600),
                            display: 'block',
                            fontSize: isSection ? '0.85rem' : (isSynopsis ? '0.8rem' : '0.95rem'),
                            fontStyle: isSynopsis ? 'italic' : 'normal',
                            color: isSynopsis ? 'var(--mui-palette-text-secondary)' : 'inherit',
                            marginLeft: indent,
                            opacity: isSynopsis ? 0.8 : 1
                          }}>
                            {isSection ? `${'#'.repeat(scene.level || 1)} ` : ''}
                            {scene.text || (isSection ? 'UNTITLED SECTION' : isSynopsis ? '(synopsis)' : 'UNTITLED SCENE')}
                          </span>
                        }
                        secondary={
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--mui-palette-text-secondary)' }}>
                            Page {scene.page}
                          </span>
                        }
                      />
                    </ListItemButton>
                    );
                  })
                )}
              </List>
            </Paper>
          )}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, '@media print': { display: 'block', flex: 'none', minWidth: 'auto', minHeight: 'auto' } }}>
            {/* Paginated inline-editable screenplay pages */}
            <Box ref={pagesContainerRef} id="screenplay-print-area" sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#1e1e1e' : '#f5f5f5',
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              '@media print': {
                display: 'block',
                position: 'absolute',
                left: 0,
                top: 0,
                backgroundColor: 'white',
                padding: 0,
                gap: 0,
                overflow: 'visible',
                minWidth: 'auto',
                flex: 'none',
                minHeight: 'auto',
                width: '8.5in'
              }
            }}>
              {renderTitlePage()}
              {(() => {
                let globalLineIndex = 0;
                return pages.map((pageLines, pageIndex) => {
                  const pageStartIndex = globalLineIndex;
                  globalLineIndex += pageLines.length;
                  // A page is a "continuation" if it doesn't start with a scene heading
                  // (i.e. the previous scene continues onto this page)
                  const startsWithScene = pageLines.some(l => l.type === 'scene');
                  const isContinuation = pageIndex > 0 && !startsWithScene;
                  // Check if this page continues to the next page (next page is also a continuation)
                  const nextPage = pages[pageIndex + 1];
                  const nextStartsWithScene = nextPage ? nextPage.some(l => l.type === 'scene') : true;
                  const continuesToNext = nextPage && !nextStartsWithScene;
                  return (
                    <Box
                      key={pageIndex}
                      className="script-page"
                      sx={{
                        width: `${MEASUREMENTS.PAGE_WIDTH}in`,
                        height: `${MEASUREMENTS.PAGE_HEIGHT}in`,
                        padding: `${MEASUREMENTS.TOP_MARGIN}in ${MEASUREMENTS.RIGHT_MARGIN}in ${MEASUREMENTS.BOTTOM_MARGIN}in ${MEASUREMENTS.LEFT_MARGIN}in`,
                        backgroundColor: isDarkMode ? '#0b0b0b' : 'white',
                        color: isDarkMode ? darkModeColors.text : '#000',
                        boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.5)' : '0 0 10px rgba(0,0,0,0.1)',
                        position: 'relative',
                        minHeight: `${MEASUREMENTS.PAGE_HEIGHT}in`,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        pageBreakAfter: 'always',
                        '@media print': {
                          boxShadow: 'none',
                          margin: 0,
                          pageBreakAfter: 'always',
                          height: `${MEASUREMENTS.PAGE_HEIGHT}in`,
                          width: `${MEASUREMENTS.PAGE_WIDTH}in`,
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                          padding: `${MEASUREMENTS.TOP_MARGIN}in ${MEASUREMENTS.RIGHT_MARGIN}in ${MEASUREMENTS.BOTTOM_MARGIN}in ${MEASUREMENTS.LEFT_MARGIN}in`
                        }
                      }}
                    >
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: `${MEASUREMENTS.PAGE_NUMBER_TOP || 0.5}in`,
                          right: `${MEASUREMENTS.RIGHT_MARGIN}in`,
                          fontFamily: 'Courier Prime, Courier, monospace',
                          fontSize: '12pt'
                        }}
                      >
                        {`${pageIndex + 1}.`}
                      </Typography>

                      {/* CONTINUED: marker at top of continuation pages */}
                      {isContinuation && (
                        <Typography
                          sx={{
                            fontFamily: 'Courier Prime, Courier, monospace',
                            fontSize: '12pt',
                            textTransform: 'uppercase',
                            marginBottom: '12pt',
                            userSelect: 'none',
                          }}
                        >
                          CONTINUED:
                        </Typography>
                      )}

                      {/* Page content — each line is inline-editable */}
                      {pageLines.map((line, index) => {
                        const originalLineIndex = line.key !== undefined ? line.key : pageStartIndex + index;
                        return renderLine(line, originalLineIndex);
                      })}

                      {/* CONTINUED: marker at bottom of pages that continue to next */}
                      {continuesToNext && (
                        <Typography
                          sx={{
                            fontFamily: 'Courier Prime, Courier, monospace',
                            fontSize: '12pt',
                            textTransform: 'uppercase',
                            marginTop: 'auto',
                            paddingTop: '12pt',
                            userSelect: 'none',
                          }}
                        >
                          CONTINUED:
                        </Typography>
                      )}
                    </Box>
                  );
                });
              })()}
            </Box>
          </Box>
        </Box>

        <Menu
          anchorEl={transitionMenuAnchor}
          open={Boolean(transitionMenuAnchor)}
          onClose={() => setTransitionMenuAnchor(null)}
        >
          {TRANSITION_OPTIONS.map((option) => (
            <MenuItem
              key={option}
              onClick={() => {
                insertSnippetAtCursor(`\n${option}`);
                setTransitionMenuAnchor(null);
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>

      <Menu
        anchorEl={shortcutAnchor}
        open={Boolean(shortcutAnchor)}
        onClose={() => setShortcutAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 320,
            p: 1,
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 0.5, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
          Formatting
        </Typography>
        {[
          { shortcut: 'Ctrl+B', label: 'Bold' },
          { shortcut: 'Ctrl+I', label: 'Italic' },
          { shortcut: 'Ctrl+U', label: 'Underline' },
        ].map(({ shortcut, label }) => (
          <MenuItem key={shortcut} disabled sx={{ opacity: 0.85, py: 0.25, minHeight: 'auto' }}>
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.7rem', ml: 3 }}>{shortcut}</Typography>
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.75 }} />
        <Typography variant="subtitle2" sx={{ px: 2, py: 0.5, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
          Elements
        </Typography>
        {[
          { shortcut: 'Ctrl+Shift+H', label: 'Scene Heading' },
          { shortcut: 'Ctrl+Shift+A', label: 'Action' },
          { shortcut: 'Ctrl+Shift+C', label: 'Character' },
          { shortcut: 'Ctrl+Shift+G', label: 'Dialogue' },
          { shortcut: 'Ctrl+Shift+P', label: 'Parenthetical' },
          { shortcut: 'Ctrl+Shift+T', label: 'Transition' },
          { shortcut: 'Ctrl+Shift+E', label: 'Centered Text' },
          { shortcut: 'Ctrl+Shift+D', label: 'Dual Dialogue' },
        ].map(({ shortcut, label }) => (
          <MenuItem key={shortcut} disabled sx={{ opacity: 0.85, py: 0.25, minHeight: 'auto' }}>
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.7rem', ml: 3 }}>{shortcut}</Typography>
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.75 }} />
        <Typography variant="subtitle2" sx={{ px: 2, py: 0.5, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
          Structure & Extras
        </Typography>
        {[
          { shortcut: 'Ctrl+Shift+L', label: 'Lyric' },
          { shortcut: 'Ctrl+Shift+Enter', label: 'Page Break' },
        ].map(({ shortcut, label }) => (
          <MenuItem key={shortcut} disabled sx={{ opacity: 0.85, py: 0.25, minHeight: 'auto' }}>
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.7rem', ml: 3 }}>{shortcut}</Typography>
          </MenuItem>
        ))}
      </Menu>

        <Menu
          anchorEl={characterMenuAnchor}
          open={Boolean(characterMenuAnchor)}
          onClose={() => setCharacterMenuAnchor(null)}
        >
          {characterNames.length === 0 ? (
            <MenuItem disabled>No characters detected yet</MenuItem>
          ) : (
            characterNames.map((name) => (
              <MenuItem
                key={name}
                onClick={() => {
                  insertSnippetAtCursor(name);
                  setCharacterMenuAnchor(null);
                }}
              >
                {name}
              </MenuItem>
            ))
          )}
          <MenuItem
            onClick={() => {
              insertSnippetAtCursor(QUICK_INSERT_SNIPPETS.character);
              setCharacterMenuAnchor(null);
            }}
            sx={{ fontStyle: 'italic', borderTop: '1px solid rgba(0,0,0,0.08)' }}
          >
            New character...
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={scenePrefixAnchor}
          open={Boolean(scenePrefixAnchor)}
          onClose={() => setScenePrefixAnchor(null)}
        >
          {SCENE_PREFIXES.map((option) => (
            <MenuItem
              key={option}
              onClick={() => {
                insertSnippetAtCursor(`${option} `);
                setScenePrefixAnchor(null);
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={locationMenuAnchor}
          open={Boolean(locationMenuAnchor)}
          onClose={() => setLocationMenuAnchor(null)}
        >
          {sceneMetadata.locations.length === 0 ? (
            <MenuItem disabled>No locations detected yet</MenuItem>
          ) : (
            sceneMetadata.locations.map((location) => (
              <MenuItem
                key={location}
                onClick={() => {
                  appendToCurrentLine(location);
                  setLocationMenuAnchor(null);
                }}
              >
                {location}
              </MenuItem>
            ))
          )}
          <MenuItem
            onClick={() => {
              appendToCurrentLine('LOCATION NAME');
              setLocationMenuAnchor(null);
            }}
            sx={{ fontStyle: 'italic', borderTop: '1px solid rgba(0,0,0,0.08)' }}
          >
            New location...
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={timeMenuAnchor}
          open={Boolean(timeMenuAnchor)}
          onClose={() => setTimeMenuAnchor(null)}
        >
          {timeOfDayOptions.map((option) => (
            <MenuItem
              key={option}
              onClick={() => {
                appendToCurrentLine(` - ${option}`);
                setTimeMenuAnchor(null);
              }}
            >
              {option}
            </MenuItem>
          ))}
          <MenuItem
            onClick={() => {
              appendToCurrentLine(' - TIME');
              setTimeMenuAnchor(null);
            }}
            sx={{ fontStyle: 'italic', borderTop: '1px solid rgba(0,0,0,0.08)' }}
          >
            Custom time...
          </MenuItem>
        </Menu>

        {/* Tag Type Selection Menu */}
        <Menu
          anchorEl={tagMenuAnchor}
          open={Boolean(tagMenuAnchor)}
          onClose={() => {
            setTagMenuAnchor(null);
            setSelectedText(null);
          }}
        >
          <Box sx={{ p: 1 }}>
            {tagMenuItems.map(({ type, label, color }) => (
              <MenuItem
                key={type}
                onClick={() => handleCreateTag(type)}
                sx={{
                  color: color,
                  mb: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: `${color}22`,
                  }
                }}
              >
                <LocalOfferIcon sx={{ mr: 1, color: color }} />
                {label}
              </MenuItem>
            ))}
          </Box>
        </Menu>

        {/* Tag Removal Confirmation Dialog */}
        <Dialog
          open={Boolean(tagRemovalConfirm)}
          onClose={() => setTagRemovalConfirm(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Remove Tag?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Remove the <strong>{tagRemovalConfirm?.label}</strong> tag from "{tagRemovalConfirm?.tag?.text}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTagRemovalConfirm(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (tagRemovalConfirm?.tag) {
                  handleRemoveTag(tagRemovalConfirm.tag.id);
                }
                setTagRemovalConfirm(null);
              }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {/* Tag Manager Dialog */}
        <Dialog 
          open={tagManagerOpen} 
          onClose={() => setTagManagerOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Tag Manager
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {tags.length} tags in script
            </Typography>
          </DialogTitle>
          <DialogContent>
            {tagMenuItems.map(({ type, label, color }) => {
              const tagsOfType = tags.filter(tag => tag.type === type);
              if (tagsOfType.length === 0) return null;

              return (
                <Box key={type} sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: color,
                      borderBottom: `2px solid ${color}`,
                      pb: 0.5,
                      mb: 1
                    }}
                  >
                    {label} ({tagsOfType.length})
                  </Typography>
                  <List dense>
                    {tagsOfType.map(tag => (
                      <ListItem
                        key={tag.id}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => handleRemoveTag(tag.id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          disableTypography
                          primary={tag.text}
                          secondary={`Line ${tag.lineNumber + 1}`}
                          primaryTypographyProps={{
                            component: 'span',
                            style: {
                              backgroundColor: `${tag.color}22`,
                              padding: '2px 4px',
                              borderRadius: '2px',
                              display: 'inline-block'
                            }
                          }}
                          secondaryTypographyProps={{ component: 'span' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
            {tags.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No tags added yet
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => setTagManagerOpen(false)}
              variant="outlined"
              size="small"
            >
              Close
            </Button>
            {tags.length > 0 && (
              <Button
                onClick={() => {
                  setTags([]);
                  if (onUpdateTags) onUpdateTags([]);
                }}
                color="error"
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon />}
              >
                Clear All Tags
              </Button>
            )}
          </DialogActions>
        </Dialog>

      </Box>

      {/* Inline autocomplete dropdown — portaled to document.body to escape overflow:hidden ancestors */}
      {/* acTick forces re-render of this block when autocomplete ref changes */}
      {acTick >= 0 && autocompleteStateRef.current.visible && autocompleteStateRef.current.pos && ReactDOM.createPortal(
        <Paper
          ref={autocompleteRef}
          elevation={6}
          sx={{
            position: 'fixed',
            top: autocompleteStateRef.current.pos.top,
            left: autocompleteStateRef.current.pos.left,
            zIndex: 1300,
            maxHeight: 220,
            minWidth: 180,
            maxWidth: 340,
            overflowY: 'auto',
            borderRadius: 1,
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
          }}
        >
          {autocompleteStateRef.current.context && (
            <Typography variant="caption" sx={{
              display: 'block',
              px: 1.5, pt: 1, pb: 0.5,
              color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.6rem',
              letterSpacing: '0.05em',
            }}>
              {autocompleteStateRef.current.context === 'prefix' ? 'Scene Prefix' :
               autocompleteStateRef.current.context === 'location' ? 'Location' :
               autocompleteStateRef.current.context === 'time' ? 'Time of Day' :
               autocompleteStateRef.current.context === 'character' ? 'Character' : ''}
            </Typography>
          )}
          {autocompleteStateRef.current.items.map((item, i) => (
            <Box
              key={item + i}
              onMouseDown={(ev) => {
                ev.preventDefault();
                applyAutocompleteSuggestion(item);
              }}
              sx={{
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                fontFamily: 'Courier Prime, Courier, monospace',
                fontSize: '11pt',
                backgroundColor: i === autocompleteStateRef.current.index
                  ? (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(25,118,210,0.12)')
                  : 'transparent',
                color: isDarkMode ? '#fff' : '#000',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                },
              }}
            >
              {item}
            </Box>
          ))}
          <Typography variant="caption" sx={{
            display: 'block',
            px: 1.5, py: 0.5,
            color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            fontSize: '0.55rem',
            borderTop: '1px solid',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          }}>
            ↑↓ Navigate · Tab/Enter Select · Esc Dismiss
          </Typography>
        </Paper>,
        document.body
      )}
    </>
  );
});

export default SimpleScreenplayViewer;
